import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import * as d3 from 'd3';
import { NgxMaskDirective, NgxMaskService } from 'ngx-mask';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MonthlyReturn, PortfolioResult } from './api-response.model';
import { InvestmentApiItem, InvestmentChoice } from './investment-choice.model';
import { INVESTMENTS_API_URL, TARGET_DATE_PORTFOLIOS, TARGET_DATE_PORTFOLIO_BENCHMARKS, DIMENSIONAL_MUTUAL_FUND_PORTFOLIOS, DIMENSIONAL_INDEX_PORTFOLIOS, DIMENSIONAL_BENCHMARK_PORTFOLIOS,  apiItemToChoice, apiItemToGlidePathChoice } from './investment-choices.data';
import { FUND_DESCRIPTION_OVERRIDES, FUND_DESCRIPTION_OVERRIDES_BY_SYMBOL } from './fund-description-overrides.data';
import { SvgDefinitionsComponent } from "../svg-definitions/svg-definitions.component";
import { SvgSpriteV2Component } from '../svg-sprite-v2/svg-sprite-v2.component';
import { FeeScheduleComponent } from '../fee-schedule/fee-schedule.component';
import { SafePipe } from 'src/app/pipes/safe.pipe';
import { BannerSliderComponent } from '../banner-slider/banner-slider.component';
import { BannerCardComponent } from '../banner-card/banner-card.component';
import { ImportantDisclosuresComponent } from '../important-disclosures/important-disclosures.component';

type ResultPeriodRow = {
  portfolioName: string;
  portfolioSymbol: string;
  totalReturn: number;
  annualizedReturn: number;
  stdDev: number | null;
  annualizedStdDev: number | null;
  endingDollar: number;
  monthly: MonthlyReturn[];
  inceptionDate: string | null;
  terminationDate: string | null;
};

@Component({
  selector: 'app-index-calculator-v2026',
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule, 
    NgxMaskDirective, 
    SvgDefinitionsComponent,
    SvgSpriteV2Component,
    MatSlideToggleModule,
    FeeScheduleComponent,
    RouterModule,
    SafePipe,
    BannerSliderComponent,
    BannerCardComponent,
    ImportantDisclosuresComponent,
  ],
  templateUrl: './index-calculator-v2026.component.html',
  styleUrl: './index-calculator-v2026.component.scss',
  providers: [NgxMaskService],
  changeDetection:ChangeDetectionStrategy.Default
})
export class IndexCalculatorV2026Component implements OnInit {

  availableInvestments: InvestmentChoice[] = [];
  investmentsLoading = true;

  // ── Data date range (from API) ────────────────────────────────────────────
  dataRangeLoading = true;
  dataRangeMin: { year: number; month: number } = { year: 1928, month: 1 };
  dataRangeMax: { year: number; month: number } = { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };

  // ── Investment picker state ──────────────────────────────────────────────
  selectedInvestmentNames: string[] = [];
  activeInvestmentCategory: string;
  pickerDropdownOpen = false;
  showAllPortfolios = false;
  expandedPicker = false;
  showFeeSchedule = false;
  advancedFeeMode = false;
  scheduleFeeRate = 0;
  feeDiscount = 0;
  feeFlatFee: number | null = null;
  /** When true (default), IFC Index Portfolios, GP, and Target Date calls use the user's
   *  fee schedule. When false, those calls are forced to fee=true, annualFee=0 (gross of
   *  IFC advisory fees) regardless of the fee schedule. */
  includeAdvisorFee = true;

  onFlatFeeChange(fee: number | null): void {
    if (fee === null) {
      // Reset — clear the explicit flat fee
      this.feeFlatFee = null;
      return;
    }
    // If the user enters a positive fee while advisor fee is off, snap it back on
    if (fee > 0 && !this.includeAdvisorFee) {
      this.feeFlatFee = fee;
      this.includeAdvisorFee = true;
      return;
    }
    // Always persist the value when advisor fee is on
    if (this.includeAdvisorFee) {
      this.feeFlatFee = fee;
    }
  }

  onDiscountChange(discount: number): void {
    this.feeDiscount = discount;
    // If the user enters a positive discount while advisor fee is off, snap it back on
    if (discount > 0 && !this.includeAdvisorFee) {
      this.includeAdvisorFee = true;
    }
  }
  showAllMonthlyRows: { [tabIndex: number]: boolean } = {};
  detailGranularity: ('monthly' | 'annual')[] = [];
  annualCycleMode: ('calendar' | 'rolling')[] = [];
  showFeeCol = false;

  readonly PORTFOLIOS_CATEGORY = 'IFC Index Portfolios';
  readonly GLIDE_PATH_CATEGORY = 'IFC Index Portfolios (Glide Path)';

  visibleInvestmentsByCategory(category: string): InvestmentChoice[] {
    const all = this.investmentsByCategory(category);
    if ((category === this.PORTFOLIOS_CATEGORY || category === this.GLIDE_PATH_CATEGORY) && !this.showAllPortfolios) {
      // Show every 5th: 100, 95, 90 … 5 (portfolios are ordered 100→1)
      return all.filter(inv => {
        const n = parseInt(inv.name.replace('IFC Index Portfolio ', ''), 10);
        return n % 5 === 0;
      });
    }
    if (category === 'Dimensional Index Portfolios' || category === 'Dimensional Prospectus Benchmark Portfolios' || category === 'Dimensional Mutual Fund Portfolios') {
      return [...all].reverse();
    }
    return all;
  }

  onPickerDropdownBlur(): void {
    setTimeout(() => { this.pickerDropdownOpen = false; }, 150);
  }

  get investmentCategories(): string[] {
    return [...new Set(this.availableInvestments.map(i => i.category))];
  }

  investmentsByCategory(category: string): InvestmentChoice[] {
    return this.availableInvestments.filter(i => i.category === category);
  }

  isSelected(name: string): boolean {
    return this.selectedInvestmentNames.includes(name);
  }

  toggleInvestment(name: string): void {
    if (this.isSelected(name)) {
      this.selectedInvestmentNames = this.selectedInvestmentNames.filter(n => n !== name);
    } else {
      this.selectedInvestmentNames = [...this.selectedInvestmentNames, name];
    }
  }

  removeSelectedInvestment(name: string): void {
    this.selectedInvestmentNames = this.selectedInvestmentNames.filter(n => n !== name);
  }

  selectedCountInCategory(category: string): number {
    return this.investmentsByCategory(category).filter(i => this.isSelected(i.name)).length;
  }

  allInCategorySelected(category: string): boolean {
    return this.visibleInvestmentsByCategory(category).every(i => this.isSelected(i.name));
  }

  someInCategorySelected(category: string): boolean {
    return this.investmentsByCategory(category).some(i => this.isSelected(i.name))
      && !this.allInCategorySelected(category);
  }

  toggleCategory(category: string): void {
    const visible = this.visibleInvestmentsByCategory(category);
    if (visible.every(i => this.isSelected(i.name))) {
      const names = visible.map(i => i.name);
      this.selectedInvestmentNames = this.selectedInvestmentNames.filter(n => !names.includes(n));
    } else {
      const toAdd = visible.map(i => i.name).filter(n => !this.isSelected(n));
      this.selectedInvestmentNames = [...this.selectedInvestmentNames, ...toAdd];
    }
  }

  toggleCategoryExpanded(category: string): void {
    this.activeInvestmentCategory = category;
  }

  // ── Category visibility (show/hide picker columns) ────────────────────────
  /** Categories whose picker column is currently hidden.
   *  Default: show IFC Index Portfolios, IFC Indexes, Target Date Retirement only. */
  private readonly DEFAULT_HIDDEN_CATEGORIES: ReadonlyArray<string> = [
    'IFC Index Portfolios (Glide Path)',
    'Target Date Benchmarks',
    'Dimensional Mutual Fund Portfolios',
    'Dimensional Index Portfolios',
    'Dimensional Prospectus Benchmark Portfolios',
  ];
  private hiddenCategories = new Set<string>(this.DEFAULT_HIDDEN_CATEGORIES);

  /** Maps each category to its fee type for the picker badge. */
  readonly categoryFeeType: Record<string, 'net' | 'gross'> = {
    'IFC Index Portfolios':                'net',
    'IFC Index Portfolios (Glide Path)':   'net',
    'IFC Indexes':                         'gross',
    'Target Date Retirement':              'net',
    'Target Date Benchmarks':              'gross',
    'Dimensional Mutual Fund Portfolios':          'gross',
    'Dimensional Index Portfolios':               'gross',
    'Dimensional Prospectus Benchmark Portfolios': 'gross',
  };

  readonly categoryDescriptions: Record<string, string> = {
    'IFC Index Portfolios':
      'For Individual Investors, Foundations & Endowments, and Retirement Plans. Risk-adjusted model index portfolios ranging from 100% Equity to 100% Fixed Income.',
    'IFC Index Portfolios (Glide Path)':
      'Performance reflects the dynamic equity allocation of each portfolio over the measured period',
    'IFC Indexes':
      'The underlying asset class indexes used to construct the IFC Index Portfolios.',
    'Target Date Retirement':
      'For Retirement Plan Participants and Plan Sponsors. Funds automatically shift from a higher equity allocation to a higher fixed income allocation as the target retirement year approaches.',
    'Target Date Benchmarks':
      'Custom blended benchmarks constructed to mirror the equity-to-fixed-income glide path of each Target Date Retirement portfolio. Each underlying fund component is replaced with its prospectus-designated benchmark.'
  };

  /** Explicit width for each category's picker column. Adjust as needed. */
  readonly categoryPickerWidth: Record<string, string> = {
    'IFC Index Portfolios':            '290px',
    'IFC Index Portfolios (Glide Path)': '270px',
    'IFC Indexes':                     '340px',
    'Target Date Retirement':          '440px',
    'Target Date Benchmarks':          '385px',
    'Dimensional Mutual Fund Portfolios':          '350px',
    'Dimensional Index Portfolios':               '340px',
    'Dimensional Prospectus Benchmark Portfolios': '400px',
  };

  /** Descriptions added for Dimensional categories */
  get categoryDescriptionsAll(): Record<string, string> {
    return {
      ...this.categoryDescriptions,
      'Dimensional Mutual Fund Portfolios':
        'A series of risk-based index portfolios constructed using Dimensional Fund Advisors (DFA) funds.',
      'Dimensional Index Portfolios':
        'A series of risk-based index portfolios constructed using Dimensional Fund Advisors (DFA) Indexes.',
      'Dimensional Prospectus Benchmark Portfolios':
        'Benchmark portfolios that mirror the Dimensional Mutual Fund Portfolios using prospectus benchmark indexes for each equivalent fund.',
    };
  }

  /** Flex-basis for each item row within a category's picker list. Adjust as needed. */
  readonly categoryItemWidth: Record<string, string> = {
    'IFC Index Portfolios':            '200px',
    'IFC Index Portfolios (Glide Path)': '240px',
    'IFC Indexes':                     '320px',
    'Target Date Retirement':          '340px',
    'Target Date Benchmarks':          '360px',
    'Dimensional Mutual Fund Portfolios':          '350px',
    'Dimensional Index Portfolios':               '340px',
    'Dimensional Prospectus Benchmark Portfolios': '380px',

  };

  isCategoryVisible(category: string): boolean {
    return !this.hiddenCategories.has(category);
  }

  toggleCategoryVisible(category: string): void {
    if (this.hiddenCategories.has(category)) {
      this.hiddenCategories.delete(category);
    } else {
      this.hiddenCategories.add(category);
    }
    this.pushCategoryParams();
  }

  /** Writes only the category-visibility state to the URL (does not require results). */
  private pushCategoryParams(): void {
    const allCats = [...new Set(this.availableInvestments.map(i => i.category))];
    const visibleCats = allCats.filter(c => this.isCategoryVisible(c));
    const defaultVisible = allCats.filter(c => !this.DEFAULT_HIDDEN_CATEGORIES.includes(c));
    // Only write the param when the selection differs from the default
    const isDefault =
      visibleCats.length === defaultVisible.length &&
      visibleCats.every(c => defaultVisible.includes(c));

    const path = this.location.path().split('?')[0];
    const existingQs = this.location.path().includes('?')
      ? this.location.path().split('?')[1] ?? ''
      : '';
    const params = new URLSearchParams(existingQs);
    if (isDefault) {
      params.delete('cats');
    } else {
      // Encode as short category keys to keep the URL tidy
      const keys = visibleCats.map(c => this.catToSlug(c)).filter(Boolean);
      params.set('cats', keys.join(','));
    }
    const qs = params.toString();
    this.location.replaceState(path, qs);
  }

  /** Short slug ↔ full category name mappings. */
  private readonly CAT_SLUG_MAP: ReadonlyArray<{ slug: string; name: string }> = [
    { slug: 'portfolios', name: 'IFC Index Portfolios' },
    { slug: 'portfolios-gp', name: 'IFC Index Portfolios (Glide Path)' },
    { slug: 'indexes', name: 'IFC Indexes' },
    { slug: 'td', name: 'Target Date Retirement' },
    { slug: 'bm', name: 'Target Date Benchmarks' },
  ];

  private catToSlug(name: string): string | null {
    return this.CAT_SLUG_MAP.find(m => m.name === name)?.slug ?? null;
  }

  private slugToCat(slug: string): string | null {
    return this.CAT_SLUG_MAP.find(m => m.slug === slug)?.name ?? null;
  }

  get startingAssetsNumber(): number {
    return +(String(this.form.get('startingAssets')?.value ?? '0').replace(/,/g, '')) || 0;
  }

  get allInvestmentsSelected(): boolean {
    return false; // unused — kept for safety
  }

  toggleAllInvestments(): void {}

  // ── Time period presets ──────────────────────────────────────────────────
  readonly presets = [
    { label: 'YTD',   months: 0,   ytd: true },
    { label: '1 Mo',  months: 1,   ytd: false },
    { label: '3 Mo',  months: 3,   ytd: false },
    { label: '6 Mo',  months: 6,   ytd: false },
    { label: '1 Yr',  months: 12,  ytd: false },
    { label: '3 Yr',  months: 36,  ytd: false },
    { label: '5 Yr',  months: 60,  ytd: false },
    { label: '10 Yr', months: 120, ytd: false },
    { label: '15 Yr', months: 180, ytd: false },
    { label: 'Since IFC Inception 1/2009', months: 0, ytd: false, fixedStart: { month: 1, year: 2009 } },
  ];

  activePresets = new Set<string>();

  get allPresetsActive(): boolean {
    return this.presets.every(p => this.activePresets.has(p.label));
  }

  toggleAllPresets(): void {
    if (this.allPresetsActive) {
      // Deselect all — remove in reverse order to keep indexes stable
      [...this.presets].reverse().forEach(p => {
        if (this.activePresets.has(p.label)) {
          this.togglePreset(p);
        }
      });
    } else {
      this.presets.forEach(p => {
        if (!this.activePresets.has(p.label)) {
          this.togglePreset(p);
        }
      });
    }
  }

  togglePreset(preset: { label: string; months: number; ytd: boolean; fixedStart?: { month: number; year: number } }): void {
    if (this.activePresets.has(preset.label)) {
      // Remove the matching time period row
      this.activePresets.delete(preset.label);
      const idx = this.presetIndexMap.get(preset.label);
      if (idx !== undefined) {
        this.timePeriods.removeAt(idx);
        this.presetIndexMap.delete(preset.label);
        // Re-index remaining preset rows
        this.activePresets.forEach(lbl => {
          const i = this.presetIndexMap.get(lbl);
          if (i !== undefined && i > idx) {
            this.presetIndexMap.set(lbl, i - 1);
          }
        });
      }
    } else {
      // Add a new time period row for this preset
      this.activePresets.add(preset.label);
      const newRow = this.buildPresetPeriod(preset);
      this.timePeriods.push(newRow);
      this.presetIndexMap.set(preset.label, this.timePeriods.length - 1);
    }
  }

  /** Maps preset label → its index in the timePeriods FormArray */
  private presetIndexMap = new Map<string, number>();

  private buildPresetPeriod(preset: { months: number; ytd: boolean; fixedStart?: { month: number; year: number } }): FormGroup {
    const endMonth = this.dataRangeMax.month;
    const endYear  = this.dataRangeMax.year;
    let startMonth: number;
    let startYear: number;
    if (preset.fixedStart) {
      startMonth = preset.fixedStart.month;
      startYear  = preset.fixedStart.year;
    } else if (preset.ytd) {
      startMonth = 1;
      startYear  = endYear;
    } else {
      const d = new Date(endYear, endMonth - preset.months, 1);
      startMonth = d.getMonth() + 1;
      startYear  = d.getFullYear();
    }
    return this.fb.group({ startMonth, startYear, endMonth, endYear, isCustom: [false] });
  }

  // ── Date-range helpers ─────────────────────────────────────────────────
  /** Months valid for the start picker at the given year. */
  startMonthsFor(year: number): { value: number; label: string }[] {
    return this.months.filter(m => {
      if (year === this.dataRangeMin.year && m.value < this.dataRangeMin.month) return false;
      if (year === this.dataRangeMax.year && m.value > this.dataRangeMax.month) return false;
      return true;
    });
  }

  /** Months valid for the end picker at the given year. */
  endMonthsFor(year: number): { value: number; label: string }[] {
    return this.startMonthsFor(year);
  }

  // ── Months list ──────────────────────────────────────────────────────────
  months = [
    { value: 1,  label: 'January' },
    { value: 2,  label: 'February' },
    { value: 3,  label: 'March' },
    { value: 4,  label: 'April' },
    { value: 5,  label: 'May' },
    { value: 6,  label: 'June' },
    { value: 7,  label: 'July' },
    { value: 8,  label: 'August' },
    { value: 9,  label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
  ) {
    this.activeInvestmentCategory = '';
    this.form = this.fb.group({
      startingAssets: [100000],
      timePeriods: this.fb.array([])
    });
  }

  ngOnInit(): void {
    // Fetch both the data date range and investment list before applying URL params,
    // so that buildPresetPeriod() uses the correct dataRangeMax (not today's date).
    const DATE_RANGE_URL = 'https://data2.ifa.com/api/v1/investment/performance/monthly/grr/?startDates=start&endDates=end&ids=646';

    const dateRange$ = this.http.get<{ investments: Array<{ periods: Array<{ subtitle: { start: string; end: string } }> }> }>(DATE_RANGE_URL);
    const investments$ = this.http.get<InvestmentApiItem[]>(INVESTMENTS_API_URL);

    forkJoin({ dateRange: dateRange$, investments: investments$ }).subscribe({
      next: ({ dateRange, investments: items }) => {
        // ── Date range ───────────────────────────────────────────
        const subtitle = dateRange?.investments?.[0]?.periods?.[0]?.subtitle;
        if (subtitle) {
          const minParts = subtitle.start.split('/').map(Number); // M/D/YYYY
          const maxParts = subtitle.end.split('/').map(Number);
          this.dataRangeMin = { year: minParts[2], month: minParts[0] };
          this.dataRangeMax = { year: maxParts[2], month: maxParts[0] };
        }
        this.dataRangeLoading = false;

        // ── Investments ──────────────────────────────────────────
        const portfolios = items
          .filter(i => i.id >= 14110 && i.id <= 14210)
          .sort((a, b) => a.id - b.id)  // sort ascending so 14110=P100 → 14210=P0
          .map(i => apiItemToChoice(i));

        const glidepathPortfolios = items
          .filter(i => i.id >= 14110 && i.id <= 14210)
          .sort((a, b) => a.id - b.id)
          .map(i => apiItemToGlidePathChoice(i));

        // IDs owned by static TARGET_DATE_PORTFOLIOS / TARGET_DATE_PORTFOLIO_BENCHMARKS —
        // exclude from indexes to avoid duplicates; color/description will be patched below.
        const staticTdIds = new Set([
          ...TARGET_DATE_PORTFOLIOS.map(e => e.id),
          ...TARGET_DATE_PORTFOLIO_BENCHMARKS.map(e => e.id),
        ].filter((id): id is number => id !== undefined));

        const indexes = items
          .filter(i => (i.id < 14110 || i.id > 14210) && !staticTdIds.has(i.id))
          .map(i => apiItemToChoice(i));

        // Patch color and description from the API onto static TD/BM entries that have an id
        const patchFromApi = (entry: InvestmentChoice): InvestmentChoice => {
          if (entry.id === undefined) return entry;
          const apiItem = items.find(i => i.id === entry.id);
          if (!apiItem) return entry;
          const hex = apiItem.color?.trim() ?? '';
          return {
            ...entry,
            color:       entry.color       || (hex ? `#${hex}` : entry.color),
            description: entry.description || apiItem.description || entry.description,
          };
        };

        const patchedTdPortfolios = TARGET_DATE_PORTFOLIOS.map(patchFromApi);
        const patchedBmPortfolios = TARGET_DATE_PORTFOLIO_BENCHMARKS.map(patchFromApi);

        this.availableInvestments = [...portfolios, ...glidepathPortfolios, ...indexes, ...patchedTdPortfolios, ...patchedBmPortfolios, ...DIMENSIONAL_MUTUAL_FUND_PORTFOLIOS ,...DIMENSIONAL_INDEX_PORTFOLIOS, ...DIMENSIONAL_BENCHMARK_PORTFOLIOS ];
        this.investmentsLoading = false;
        this.activeInvestmentCategory = this.investmentCategories[0] ?? '';

        // Restore state from URL query params — dataRangeMax is now correct
        this.applyUrlParams();
        this.cdr.detectChanges();

        // Auto-submit when the URL already carries investments + periods
        const qp = this.route.snapshot.queryParams;
        if (qp['investments'] && qp['periods']) {
          this.submit();
        }
      },
      error: () => {
        this.dataRangeLoading = false;
        this.investmentsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  get timePeriods(): FormArray {
    return this.form.get('timePeriods') as FormArray;
  }

  newTimePeriod(): FormGroup {
    return this.fb.group({
      startMonth: [1],
      startYear: [2000],
      endMonth: [12],
      endYear: [2025],
      isCustom: [true]
    });
  }

  addTimePeriod(): void {
    this.timePeriods.push(this.newTimePeriod());
  }

  removeTimePeriod(index: number): void {
    this.timePeriods.removeAt(index);
    // Clear any preset that was tracking this index
    this.activePresets.forEach(lbl => {
      if (this.presetIndexMap.get(lbl) === index) {
        this.activePresets.delete(lbl);
        this.presetIndexMap.delete(lbl);
      } else {
        const i = this.presetIndexMap.get(lbl);
        if (i !== undefined && i > index) {
          this.presetIndexMap.set(lbl, i - 1);
        }
      }
    });
  }

  apiUrl: string | null = null;
  apiResult: PortfolioResult[] | null = null;
  apiError: string | null = null;
  loading = false;
  loadingTotal = 0;
  loadingDone  = 0;
  private submitSub?: Subscription;

  pivotRows: Array<{
    portfolioName: string;
    portfolioSymbol: string;
    inceptionDate: string | null;
    terminationDate: string | null;
    cells: Array<{ totalReturn: number; annualizedReturn: number; months: number } | null>;
  }> = [];

  resultPeriods: Array<{
    label: string;
    startDate: string;
    endDate: string;
    months: number;
    rows: Array<{
      portfolioName: string;
      portfolioSymbol: string;
      totalReturn: number;
      annualizedReturn: number;
      stdDev: number | null;
      annualizedStdDev: number | null;
      endingDollar: number;
      monthly: MonthlyReturn[];
      inceptionDate: string | null;
      terminationDate: string | null;
    }>;
  }> = [];

  /** Animated snapshot of rows per period tab, used by the template during count-up. */
  displayedRows: { [tabIndex: number]: ResultPeriodRow[] } = {};
  private _countAnimRaf: number | null = null;

  private startCountAnimation(tabIndex: number): void {
    if (this._countAnimRaf !== null) {
      cancelAnimationFrame(this._countAnimRaf);
      this._countAnimRaf = null;
    }
    const target = this.resultPeriods[tabIndex]?.rows;
    if (!target?.length) return;

    const duration = 450;
    const start = performance.now();

    // Initialise to zero
    this.displayedRows[tabIndex] = target.map(r => ({
      ...r,
      totalReturn:       0,
      annualizedReturn:  0,
      stdDev:            r.stdDev           !== null ? 0 : null,
      annualizedStdDev:  r.annualizedStdDev !== null ? 0 : null,
      endingDollar:      0,
    }));

    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      // Ease-out cubic
      const e = 1 - Math.pow(1 - t, 3);
      this.displayedRows[tabIndex] = target.map(r => ({
        ...r,
        totalReturn:      r.totalReturn      * e,
        annualizedReturn: r.annualizedReturn  * e,
        stdDev:           r.stdDev           !== null ? r.stdDev           * e : null,
        annualizedStdDev: r.annualizedStdDev !== null ? r.annualizedStdDev * e : null,
        endingDollar:     r.endingDollar     * e,
      }));
      this.cdr.detectChanges();
      if (t < 1) {
        this._countAnimRaf = requestAnimationFrame(tick);
      } else {
        this.displayedRows[tabIndex] = target;
        this._countAnimRaf = null;
        this.cdr.detectChanges();
      }
    };
    this._countAnimRaf = requestAnimationFrame(tick);
  }

  private formatDate(month: number, year: number, lastDay: boolean): string {
    if (lastDay) {
      const day = new Date(year, month, 0).getDate();
      return `${month}/${day}/${year}`;
    }
    return `${month}/1/${year}`;
  }

  buildApiUrl(): string | null {
    if (!this.selectedInvestmentNames.length || !this.timePeriods.length) return null;

    const periods = this.timePeriods.controls.map(c => c.value);

    const startDates = periods.map(p => this.formatDate(p.startMonth, p.startYear, false));
    const endDates   = periods.map(p => this.formatDate(p.endMonth,   p.endYear,   true));

    const startMs = periods.map(p => new Date(p.startYear, p.startMonth - 1, 1).getTime());
    const endMs   = periods.map(p => new Date(p.endYear,   p.endMonth,       0).getTime());
    const domainStart = this.formatDate(
      new Date(Math.min(...startMs)).getMonth() + 1,
      new Date(Math.min(...startMs)).getFullYear(), false);
    const domainEndDate = new Date(Math.max(...endMs));
    const domainEnd = this.formatDate(
      domainEndDate.getMonth() + 1, domainEndDate.getFullYear(), true);

    const portfolios = this.selectedInvestmentNames
      .map(n => this.availableInvestments.find(i => i.name === n)?.symbol)
      .filter(Boolean)
      .join('|');

    const rawAssets = String(this.form.get('startingAssets')?.value ?? '').replace(/,/g, '');

    const params = new HttpParams()
      .set('domainStart',     domainStart)
      .set('domainEnd',       domainEnd)
      .set('startDate',       startDates.join('|'))
      .set('endDate',         endDates.join('|'))
      .set('portfolios',      portfolios)
      .set('glidePath',       'false')
      .set('fee',             'false')
      .set('dollar',          rawAssets)
      .set('annualFee',       '')
      .set('customGlidePath', '')
      .set('showReturns',     'true');

    return `https://data2.ifa.com/odata/v1/returns/monthly/domain?${params.toString()}`;
  }

  submit(): void {
    this.apiResult     = null;
    this.apiError      = null;
    this.resultPeriods = [];
    this.pivotRows     = [];

    if (!this.selectedInvestmentNames.length || !this.timePeriods.length) return;

    const periods    = this.timePeriods.controls.map(c => c.value);
    const startDates = periods.map(p => this.formatDate(p.startMonth, p.startYear, false));
    const startMs    = periods.map(p => new Date(p.startYear, p.startMonth - 1, 1).getTime());
    const dataRangeMaxMs = new Date(this.dataRangeMax.year, this.dataRangeMax.month, 0).getTime();
    const endMs      = periods.map(p => Math.min(new Date(p.endYear, p.endMonth, 0).getTime(), dataRangeMaxMs));
    const endDates   = endMs.map(ms => {
      const d = new Date(ms);
      return this.formatDate(d.getMonth() + 1, d.getFullYear(), true);
    });
    const domainEndDate = new Date(Math.max(...endMs));
    const domainEnd  = this.formatDate(domainEndDate.getMonth() + 1, domainEndDate.getFullYear(), true);
    const rawAssets  = String(this.form.get('startingAssets')?.value ?? '').replace(/,/g, '');

    // Determine fee params. null = omit the parameter (server uses its own schedule).
    // Only explicit user overrides (flat fee, discount, or fee disabled) are sent.
    let feeParam: string | null;
    let annualFeeParam: string | null;
    let feeDiscountPercentParam: string | null;
    if (this.feeFlatFee !== null) {
      feeParam                = 'true';
      annualFeeParam          = String(this.feeFlatFee);
      feeDiscountPercentParam = null;
    } else if (this.feeDiscount > 0) {
      feeParam                = 'true';
      annualFeeParam          = null;
      feeDiscountPercentParam = String(this.feeDiscount / 100);
    } else {
      // No user override — omit fee params entirely; server applies schedule from dollar amount.
      feeParam                = null;
      annualFeeParam          = null;
      feeDiscountPercentParam = null;
    }

    // When includeAdvisorFee is off: explicitly send fee=true, annualFee=0 to strip the fee.
    const ifcFeeParam: string | null                = this.includeAdvisorFee ? feeParam                : 'true';
    const ifcAnnualFeeParam: string | null          = this.includeAdvisorFee ? annualFeeParam          : '0';
    const ifcFeeDiscountPercentParam: string | null = this.includeAdvisorFee ? feeDiscountPercentParam : null;

    // Conditionally adds fee params — omits each one when null so the server uses its own defaults.
    const applyFee = (p: HttpParams, fee: string | null, annual: string | null, discount: string | null): HttpParams => {
      if (fee      !== null) p = p.set('fee',                fee);
      if (annual   !== null) p = p.set('annualFee',          annual);
      if (discount !== null) p = p.set('feeDiscountPercent', discount);
      return p;
    };

    const baseUrl = 'https://data2.ifa.com/odata/v1/returns/monthly/domain';

    // Split selected investments into regular vs target date
    const selectedInvestments = this.selectedInvestmentNames
      .map(n => this.availableInvestments.find(i => i.name === n))
      .filter(Boolean) as InvestmentChoice[];

    const regularInvestments    = selectedInvestments.filter(i => i.category !== 'Target Date Retirement' && i.category !== 'Target Date Benchmarks' && i.category !== this.GLIDE_PATH_CATEGORY && i.category !== 'Dimensional Index Portfolios' && i.category !== 'Dimensional Prospectus Benchmark Portfolios' && i.category !== 'Dimensional Mutual Fund Portfolios');
    const targetDateInvestments = selectedInvestments.filter(i => i.category === 'Target Date Retirement');
    const benchmarkInvestments  = selectedInvestments.filter(i => i.category === 'Target Date Benchmarks');
    const glidepathInvestments  = selectedInvestments.filter(i => i.category === this.GLIDE_PATH_CATEGORY);
    const dimensionalInvestments = selectedInvestments.filter(i => i.category === 'Dimensional Index Portfolios' || i.category === 'Dimensional Prospectus Benchmark Portfolios');
    const mutualFundInvestments  = selectedInvestments.filter(i => i.category === 'Dimensional Mutual Fund Portfolios');
    // Risk Based portfolios use direct API calls (no glide path, no vintage-derived domain start)
    const tdGlidePath = targetDateInvestments.filter(i => i.id === undefined);
    const tdRiskBased = targetDateInvestments.filter(i => i.id !== undefined);
    const bmGlidePath = benchmarkInvestments.filter(i => i.id === undefined);
    const bmRiskBased = benchmarkInvestments.filter(i => i.id !== undefined);

    // ── Regular investments call ─────────────────────────────────────────────
    let regularCall$: Observable<PortfolioResult[]>;
    if (regularInvestments.length) {
      const domainStart = this.formatDate(
        new Date(Math.min(...startMs)).getMonth() + 1,
        new Date(Math.min(...startMs)).getFullYear(), false);
      const portfolios = regularInvestments.map(i => i.symbol).join('|');
      let params = applyFee(
        new HttpParams()
          .set('domainStart',   domainStart)
          .set('domainEnd',     domainEnd)
          .set('startDate',     startDates.join('|'))
          .set('endDate',       endDates.join('|'))
          .set('portfolios',    portfolios)
          .set('glidePath',     'false')
          .set('dollar',        rawAssets)
          .set('customGlidePath', '')
          .set('showReturns',   'true'),
        ifcFeeParam, ifcAnnualFeeParam, ifcFeeDiscountPercentParam);
      this.apiUrl = `${baseUrl}?${params.toString()}`;
      regularCall$ = this.http.get<PortfolioResult[]>(`${baseUrl}?${params.toString()}`)
    } else {
      this.apiUrl = null;
      regularCall$ = of([]);
    }

    // ── IFC Index Portfolios (Glide Path) ─────────────────────────────────────
    // All GP investments in a single call with glidePath=true.
    let glidepathCall$: Observable<PortfolioResult[]>;
    if (glidepathInvestments.length) {
      const domainStart = this.formatDate(
        new Date(Math.min(...startMs)).getMonth() + 1,
        new Date(Math.min(...startMs)).getFullYear(), false);
      // Strip the _gp suffix to obtain the actual API portfolio symbols
      const gpPortfolios = glidepathInvestments.map(i => i.symbol.replace(/_gp$/, '')).join('|');
      const gpParams = applyFee(
        new HttpParams()
          .set('domainStart',   domainStart)
          .set('domainEnd',     domainEnd)
          .set('startDate',     startDates.join('|'))
          .set('endDate',       endDates.join('|'))
          .set('portfolios',    gpPortfolios)
          .set('glidePath',     'true')
          .set('dollar',        rawAssets)
          .set('customGlidePath', '')
          .set('showReturns',   'true'),
        ifcFeeParam, ifcAnnualFeeParam, ifcFeeDiscountPercentParam);
      glidepathCall$ = this.http.get<PortfolioResult[]>(`${baseUrl}?${gpParams.toString()}`)
    } else {
      glidepathCall$ = of([]);
    }

    // ── One call per target date vintage ─────────────────────────────────────
    // Domain start = 7/1/(vintageYear - 84)  e.g. 2025 → 1941, 2020 → 1936
    const tdCallInfos: Array<{ symbol: string; obs: Observable<PortfolioResult[]> }> =
      tdGlidePath.map(inv => {
        const vintageYear    = parseInt(inv.symbol.replace('td', ''), 10);
        const tdDomainStart  = `7/1/${vintageYear - 84}`;
        const tdDomainStartMs = new Date(vintageYear - 84, 6, 1).getTime();
        // Clamp each period's startDate to the TD domain start
        const clampedStartDates = startDates.map((sd, j) =>
          startMs[j] < tdDomainStartMs ? tdDomainStart : sd
        );
        const params = applyFee(
          new HttpParams()
            .set('domainStart',   tdDomainStart)
            .set('domainEnd',     domainEnd)
            .set('startDate',     clampedStartDates.join('|'))
            .set('endDate',       endDates.join('|'))
            .set('portfolios',    'port92srwf_ifa')
            .set('glidePath',     'true')
            .set('dollar',        rawAssets)
            .set('customGlidePath', 'SRWFTargetDate')
            .set('showReturns',   'true'),
          ifcFeeParam, ifcAnnualFeeParam, ifcFeeDiscountPercentParam);
        return {
          symbol: inv.symbol,
          obs: this.http.get<PortfolioResult[]>(`${baseUrl}?${params.toString()}`)
        };
      });

    // ── One call per benchmark vintage ───────────────────────────────────────
    const bmCallInfos: Array<{ symbol: string; obs: Observable<PortfolioResult[]> }> =
      bmGlidePath.map(inv => {
        const vintageYear   = parseInt(inv.symbol.replace('bm', ''), 10);
        const bmDomainStart = `7/1/${vintageYear - 84}`;
        const bmDomainStartMs = new Date(vintageYear - 84, 6, 1).getTime();
        const clampedStartDates = startDates.map((sd, j) =>
          startMs[j] < bmDomainStartMs ? bmDomainStart : sd
        );
        const params = applyFee(
          new HttpParams()
            .set('domainStart',   bmDomainStart)
            .set('domainEnd',     domainEnd)
            .set('startDate',     clampedStartDates.join('|'))
            .set('endDate',       endDates.join('|'))
            .set('portfolios',    'port92srwfbm_ifa')
            .set('glidePath',     'true')
            .set('dollar',        rawAssets)
            .set('customGlidePath', 'SRWFTargetDate')
            .set('showReturns',   'true'),
          feeParam, annualFeeParam, feeDiscountPercentParam);
        return {
          symbol: inv.symbol,
          obs: this.http.get<PortfolioResult[]>(`${baseUrl}?${params.toString()}`)
        };
      });

    // ── Risk Based Target Date Retirement (no inception, no glide path) ──────
    let tdRbCall$: Observable<PortfolioResult[]>;
    if (tdRiskBased.length) {
      const domainStart = this.formatDate(
        new Date(Math.min(...startMs)).getMonth() + 1,
        new Date(Math.min(...startMs)).getFullYear(), false);
      const params = applyFee(
        new HttpParams()
          .set('domainStart',   domainStart)
          .set('domainEnd',     domainEnd)
          .set('startDate',     startDates.join('|'))
          .set('endDate',       endDates.join('|'))
          .set('portfolios',    tdRiskBased.map(i => i.symbol).join('|'))
          .set('glidePath',     'false')
          .set('dollar',        rawAssets)
          .set('customGlidePath', '')
          .set('showReturns',   'true'),
        ifcFeeParam, ifcAnnualFeeParam, ifcFeeDiscountPercentParam);
      tdRbCall$ = this.http.get<PortfolioResult[]>(`${baseUrl}?${params.toString()}`);
    } else {
      tdRbCall$ = of([]);
    }

    // ── Risk Based Target Date Benchmarks (inception 2/1/2002, no glide path) ─
    let bmRbCall$: Observable<PortfolioResult[]>;
    if (bmRiskBased.length) {
      const rbBmInceptionMs = new Date(2002, 1, 1).getTime();
      const rbBmDomainStart = '2/1/2002';
      const clampedStartDates = startDates.map((sd, j) =>
        startMs[j] < rbBmInceptionMs ? rbBmDomainStart : sd
      );
      const domainStartMs = Math.max(Math.min(...startMs), rbBmInceptionMs);
      const domainStartDate = new Date(domainStartMs);
      const domainStart = this.formatDate(domainStartDate.getMonth() + 1, domainStartDate.getFullYear(), false);
      const params = applyFee(
        new HttpParams()
          .set('domainStart',   domainStart)
          .set('domainEnd',     domainEnd)
          .set('startDate',     clampedStartDates.join('|'))
          .set('endDate',       endDates.join('|'))
          .set('portfolios',    bmRiskBased.map(i => i.symbol).join('|'))
          .set('glidePath',     'false')
          .set('dollar',        rawAssets)
          .set('customGlidePath', '')
          .set('showReturns',   'true'),
        feeParam, annualFeeParam, feeDiscountPercentParam);
      bmRbCall$ = this.http.get<PortfolioResult[]>(`${baseUrl}?${params.toString()}`);
    } else {
      bmRbCall$ = of([]);
    }

    // ── Dimensional Index + Benchmark (always gross — fee: 'false') ──────────
    let dimensionalCall$: Observable<PortfolioResult[]>;
    if (dimensionalInvestments.length) {
      const domainStart = this.formatDate(
        new Date(Math.min(...startMs)).getMonth() + 1,
        new Date(Math.min(...startMs)).getFullYear(), false);
      const dimParams = new HttpParams()
        .set('domainStart',     domainStart)
        .set('domainEnd',       domainEnd)
        .set('startDate',       startDates.join('|'))
        .set('endDate',         endDates.join('|'))
        .set('portfolios',      dimensionalInvestments.map(i => i.symbol).join('|'))
        .set('glidePath',       'false')
        .set('fee',             'false')
        .set('dollar',          rawAssets)
        .set('annualFee',       '')
        .set('customGlidePath', '')
        .set('showReturns',     'true');
      dimensionalCall$ = this.http.get<PortfolioResult[]>(`${baseUrl}?${dimParams.toString()}`);
    } else {
      dimensionalCall$ = of([]);
    }

    // ── Dimensional Mutual Fund Portfolios (fee: 'true', annualFee: '0', gross) ─
    let mutualFundCall$: Observable<PortfolioResult[]>;
    if (mutualFundInvestments.length) {
      const domainStart = this.formatDate(
        new Date(Math.min(...startMs)).getMonth() + 1,
        new Date(Math.min(...startMs)).getFullYear(), false);
      const mfParams = new HttpParams()
        .set('domainStart',     domainStart)
        .set('domainEnd',       domainEnd)
        .set('startDate',       startDates.join('|'))
        .set('endDate',         endDates.join('|'))
        .set('portfolios',      mutualFundInvestments.map(i => i.symbol.replace(/^dfamf(\d+)$/, 'port$1sr_ifa')).join('|'))
        .set('glidePath',       'false')
        .set('fee',             'true')
        .set('dollar',          rawAssets)
        .set('annualFee',       '0')
        .set('customGlidePath', '')
        .set('showReturns',     'true');
      mutualFundCall$ = this.http.get<PortfolioResult[]>(`${baseUrl}?${mfParams.toString()}`);
    } else {
      mutualFundCall$ = of([]);
    }

    this.submitSub?.unsubscribe();

    this.loading = true;
    this.loadingTotal = 6 + tdCallInfos.length + bmCallInfos.length; // regular + glidepath + tdRb + bmRb + dimensional + mutualFund + td + bm
    this.loadingDone  = 0;
    this.cdr.detectChanges();

    const trackDone = <T>(obs: Observable<T>): Observable<T> =>
      obs.pipe(tap(() => { this.loadingDone++; this.cdr.detectChanges(); }));

    this.submitSub = forkJoin([
      trackDone(regularCall$),
      trackDone(glidepathCall$),
      trackDone(tdRbCall$),
      trackDone(bmRbCall$),
      trackDone(dimensionalCall$),
      trackDone(mutualFundCall$),
      ...tdCallInfos.map(t => trackDone(t.obs)),
      ...bmCallInfos.map(b => trackDone(b.obs))
    ]).subscribe({
      next: results => {
        this.loading = false;
        this.cdr.detectChanges();
        const regularResults = (Array.isArray(results[0]) ? results[0] : []) as PortfolioResult[];
        // Rename returned GP portfolio symbols (e.g. port100srwf_ifa) back to _gp variants
        const gpResults: PortfolioResult[] = [];
        (Array.isArray(results[1]) ? results[1] : []).forEach((r: PortfolioResult) => {
          const gpInv = glidepathInvestments.find(i => i.symbol.replace(/_gp$/, '') === r.portfolio);
          if (gpInv) {
            gpResults.push({ ...r, portfolio: gpInv.symbol });
          }
        });
        // Risk Based TD results — symbols already correct from API
        const tdRbResults = (Array.isArray(results[2]) ? results[2] : []) as PortfolioResult[];
        // Risk Based BM results — symbols already correct from API
        const bmRbResults = (Array.isArray(results[3]) ? results[3] : []) as PortfolioResult[];
        // Dimensional results — symbols already correct from API
        const dimensionalResults = (Array.isArray(results[4]) ? results[4] : []) as PortfolioResult[];
        // Mutual Fund results — remap port{n}sr_ifa → dfamf{n} so icon/color lookups work
        const mutualFundResults: PortfolioResult[] = [];
        (Array.isArray(results[5]) ? results[5] : []).forEach((r: PortfolioResult) => {
          const mfInv = mutualFundInvestments.find(
            i => i.symbol.replace(/^dfamf(\d+)$/, 'port$1sr_ifa') === r.portfolio
          );
          mutualFundResults.push(mfInv ? { ...r, portfolio: mfInv.symbol } : r);
        });
        // Rename port92srwf_ifa → tdYYYY so display name/color lookups work
        const tdResults: PortfolioResult[] = [];
        tdCallInfos.forEach((td, i) => {
          const res = results[i + 6];
          (Array.isArray(res) ? res : []).forEach((r: PortfolioResult) => {
            tdResults.push({ ...r, portfolio: td.symbol });
          });
        });
        // Rename port92srwfbm_ifa → bmYYYY
        const bmResults: PortfolioResult[] = [];
        bmCallInfos.forEach((bm, i) => {
          const res = results[6 + tdCallInfos.length + i];
          (Array.isArray(res) ? res : []).forEach((r: PortfolioResult) => {
            bmResults.push({ ...r, portfolio: bm.symbol });
          });
        });
        this.apiResult = [...regularResults, ...gpResults, ...tdRbResults, ...bmRbResults, ...dimensionalResults, ...mutualFundResults, ...tdResults, ...bmResults];
        this.activePeriodTab = 0;
        this.monthlyViewMode = [];
        this.showAllMonthlyRows = {};
        // Preserve granularity, cycle, and log scale across recalculates by pre-populating
        // the pending state from current values (pending state is only set from URL on page load;
        // on a user-initiated recalculate it is null so we fill it here before the reset).
        if (this._pendingGranularity === null) {
          const currentSlugs = this.periodSlugs();
          const annualSlugs = currentSlugs.filter((_, i) => this.detailGranularity[i] === 'annual');
          if (annualSlugs.length) this._pendingGranularity = annualSlugs.join(',');
        }
        if (this._pendingCycle === null) {
          const currentSlugs = this.periodSlugs();
          const rollingSlugs = currentSlugs.filter((_, i) => this.annualCycleMode[i] === 'rolling');
          if (rollingSlugs.length) this._pendingCycle = rollingSlugs.join(',');
        }
        if (this._pendingLog === null) {
          const currentSlugs = this.periodSlugs();
          const logSlugs = currentSlugs.filter((_, i) => this.chartLogScale[i] === true);
          if (logSlugs.length) this._pendingLog = logSlugs.join(',');
        }
        this.detailGranularity = [];
        this.annualCycleMode = [];
        this.chartLogScale = [];
        this.computeResultPeriods();
        this.computePivotRows();
        this.computeFundDescriptions();

        // Apply pending tab / detail mode (set by applyUrlParams before submit)
        if (this._pendingTab !== null) {
          const slugs = this.periodSlugs();
          const idx = slugs.indexOf(this._pendingTab);
          if (idx >= 0) this.activePeriodTab = idx;
          this._pendingTab = null;
        }
        if (this._pendingDetail !== null) {
          const parts = this._pendingDetail.split(',');
          const validCols = parts.filter(c => ['value', 'return', 'equity'].includes(c)) as ('value' | 'return' | 'equity')[];
          if (validCols.length > 0) {
            this.monthlyViewMode[this.activePeriodTab] = {
              value:  validCols.includes('value'),
              return: validCols.includes('return'),
              equity: validCols.includes('equity')
            };
          }
          this._pendingDetail = null;
        }
        if (this._pendingLog !== null) {
          const slugs = this.periodSlugs();
          this._pendingLog.split(',').forEach(s => {
            const idx = slugs.indexOf(s.trim());
            if (idx >= 0) this.chartLogScale[idx] = true;
          });
          this._pendingLog = null;
        }
        if (this._pendingGranularity !== null) {
          const slugs = this.periodSlugs();
          this._pendingGranularity.split(',').forEach(s => {
            const idx = slugs.indexOf(s.trim());
            if (idx >= 0) this.detailGranularity[idx] = 'annual';
          });
          this._pendingGranularity = null;
        }
        if (this._pendingCycle !== null) {
          const slugs = this.periodSlugs();
          this._pendingCycle.split(',').forEach(s => {
            const idx = slugs.indexOf(s.trim());
            if (idx >= 0) this.annualCycleMode[idx] = 'rolling';
          });
          this._pendingCycle = null;
        }
        if (this._pendingAllRows) {
          this.showAllMonthlyRows[this.activePeriodTab] = true;
          this._pendingAllRows = null;
        }
        if (this._pendingShowFeeCol) {
          this.showFeeCol = true;
          this._pendingShowFeeCol = null;
        }

        this.pushUrlParams();
        this.cdr.detectChanges();
        setTimeout(() => {
          this.renderCharts();
          this.startCountAnimation(this.activePeriodTab);
          this.cdr.detectChanges();
        }, 0);
      },
      error: err => { this.apiError = err.message; this.loading = false; this.cdr.detectChanges(); }
    });
  }

  // ── Results processing ────────────────────────────────────────────────────

  private computeResultPeriods(): void {
    if (!this.apiResult?.length) { this.resultPeriods = []; return; }

    const invOrder = (symbol: string) => {
      // Prefer the selected investment's position so that funds sharing a symbol
      // (e.g. "Benchmark Risk Based Index Portfolio 80" and
      // "Dimensional Prospectus Benchmark Portfolio 80") sort with their category peers.
      const selectedIdx = this.availableInvestments.findIndex(
        inv => inv.symbol === symbol && this.selectedInvestmentNames.includes(inv.name)
      );
      if (selectedIdx !== -1) return selectedIdx;
      const idx = this.availableInvestments.findIndex(inv => inv.symbol === symbol);
      return idx === -1 ? this.availableInvestments.length : idx;
    };

    const maxPeriods = Math.max(...this.apiResult.map(p => p.periods.length));
    this.resultPeriods = Array.from({ length: maxPeriods }, (_, i) => {
      const ref = this.apiResult!.find(p => p.periods[i])?.periods[i];
      // Use the actual number of monthly data points as the definitive month count
      const months = ref?.monthly?.length ?? ref?.months ?? 0;
      const periodStartDate = ref?.startDate ?? '';
      return {
        label:     this.periodLabel(months),
        startDate: periodStartDate,
        endDate:   ref?.endDate ?? '',
        months,
        rows: this.apiResult!
          .filter(p => p.periods[i])
          .sort((a, b) => invOrder(a.portfolio) - invOrder(b.portfolio))
          .map(p => ({
            portfolioName:    this.getPortfolioDisplayName(p.portfolio),
            portfolioSymbol:  p.portfolio,
            totalReturn:      p.periods[i].totalReturn,
            annualizedReturn: p.periods[i].annualizedReturn,
            stdDev:           p.periods[i].stdDev,
            annualizedStdDev: p.periods[i].annualizedStdDev,
            endingDollar:     p.periods[i].endingDollar,
            monthly:          p.periods[i].monthly,
            inceptionDate:    this.computeInceptionDate(p.periods[i], periodStartDate, p.portfolio),
            terminationDate:  this.computeTerminationDate(p.periods[i]),
          }))
      };
    });
  }

  /** Returns the fund's first data date if it is later than the period start (partial data), otherwise null. */
  private computeInceptionDate(period: import('./api-response.model').Period, periodStartDate: string, portfolioSymbol: string): string | null {
    // BM funds have a known fixed inception date of 2/1/2002. The API does not pad
    // pre-inception months with null returns, so we use a hardcoded comparison.
    if (portfolioSymbol.startsWith('bm')) {
      const bmInception = new Date(2002, 1, 1); // Feb 1, 2002
      const periodStart = periodStartDate ? new Date(periodStartDate) : null;
      if (!periodStart || isNaN(periodStart.getTime()) || periodStart >= bmInception) return null;
      return bmInception.toISOString();
    }

    // Risk Based BM funds (port80srwfbm_ifa etc.) share the same inception date as BM: 2/1/2002
    if (portfolioSymbol.startsWith('port') && portfolioSymbol.endsWith('bm_ifa')) {
      const rbBmInception = new Date(2002, 1, 1); // Feb 1, 2002
      const periodStart = periodStartDate ? new Date(periodStartDate) : null;
      if (!periodStart || isNaN(periodStart.getTime()) || periodStart >= rbBmInception) return null;
      return rbBmInception.toISOString();
    }

    // TD funds have a vintage-derived inception date of 7/1/(vintageYear - 84).
    // e.g. td2020 → 7/1/1936.  The API also does not pad pre-inception months.
    if (portfolioSymbol.startsWith('td')) {
      const vintageYear = parseInt(portfolioSymbol.replace('td', ''), 10);
      if (!isNaN(vintageYear)) {
        const tdInception = new Date(vintageYear - 84, 6, 1); // July 1
        const periodStart = periodStartDate ? new Date(periodStartDate) : null;
        if (!periodStart || isNaN(periodStart.getTime()) || periodStart >= tdInception) return null;
        return tdInception.toISOString();
      }
    }

    // Standard detection: any monthly entry with return === null is prior to the
    // fund's inception date. Find the first entry with a non-null return.
    const monthly = period.monthly;
    if (!monthly?.length) return null;
    if (monthly[0].return !== null) return null;
    const firstLiveIndex = monthly.findIndex(m => m.return !== null);
    if (firstLiveIndex <= 0) return null;
    const d = new Date(monthly[firstLiveIndex].date);
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
  }

  /** Returns the fund's last data date if it is earlier than the period end (data stops before period end), otherwise null. */
  private computeTerminationDate(period: import('./api-response.model').Period): string | null {
    const monthly = period.monthly;
    if (!monthly?.length) return null;
    // If the last entry has a non-null return, data runs to the end of the period.
    if (monthly[monthly.length - 1].return !== null) return null;
    // Walk backwards to find the last entry with a non-null return.
    let lastLiveIndex = monthly.length - 1;
    while (lastLiveIndex >= 0 && monthly[lastLiveIndex].return === null) lastLiveIndex--;
    if (lastLiveIndex < 0) return null; // entire series is null — covered by inceptionDate
    const d = new Date(monthly[lastLiveIndex].date);
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
  }

  /** True when at least one fund in the given period tab has partial data (starts after the period start). */
  hasPartialData(periodIndex: number): boolean {    return this.resultPeriods[periodIndex]?.rows.some(r => r.inceptionDate !== null) ?? false;
  }

  /** True when ANY period has at least one fund with partial data. */
  hasAnyPartialData(): boolean {
    return this.resultPeriods.some((_, i) => this.hasPartialData(i));
  }

  /** Deduplicated list of funds with partial data, for the warning block. Each fund appears once with its earliest inception date. */
  get partialDataFundSummary(): Array<{ name: string; symbol: string; inceptionDate: string }> {
    const map = new Map<string, { name: string; symbol: string; inceptionDate: string }>();
    for (const period of this.resultPeriods) {
      for (const row of period.rows) {
        if (!row.inceptionDate) continue;
        const existing = map.get(row.portfolioSymbol);
        if (!existing || new Date(row.inceptionDate).getTime() < new Date(existing.inceptionDate).getTime()) {
          map.set(row.portfolioSymbol, { name: row.portfolioName, symbol: row.portfolioSymbol, inceptionDate: row.inceptionDate });
        }
      }
    }
    return Array.from(map.values());
  }

  /** True when at least one fund in the given period tab has a termination date (data ends before the period end). */
  hasTerminatedData(periodIndex: number): boolean {
    return this.resultPeriods[periodIndex]?.rows.some(r => r.terminationDate !== null) ?? false;
  }

  /** True when ANY period has at least one fund with terminated data. */
  hasAnyTerminatedData(): boolean {
    return this.resultPeriods.some((_, i) => this.hasTerminatedData(i));
  }

  /** Deduplicated list of funds with terminated data, each appearing once with its latest termination date. */
  get terminatedDataFundSummary(): Array<{ name: string; symbol: string; terminationDate: string }> {
    const map = new Map<string, { name: string; symbol: string; terminationDate: string }>();
    for (const period of this.resultPeriods) {
      for (const row of period.rows) {
        if (!row.terminationDate) continue;
        const existing = map.get(row.portfolioSymbol);
        if (!existing || new Date(row.terminationDate).getTime() > new Date(existing.terminationDate).getTime()) {
          map.set(row.portfolioSymbol, { name: row.portfolioName, symbol: row.portfolioSymbol, terminationDate: row.terminationDate });
        }
      }
    }
    return Array.from(map.values());
  }

  fundDescriptions: Array<Array<{
    symbol: string;
    name: string;
    description: string;
    equityMin: number | null;
    equityMax: number | null;
  }>> = [];

  private computeFundDescriptions(): void {
    if (!this.resultPeriods.length) { this.fundDescriptions = []; return; }
    this.fundDescriptions = this.resultPeriods.map(period => {
      return period.rows.map(row => {
        const inv = this.availableInvestments.find(i => i.symbol === row.portfolioSymbol);
        const allEquity = row.monthly
          .map(m => this.parseEquity(m.symbol))
          .filter((e): e is number => e !== null);
        return {
          symbol:      row.portfolioSymbol,
          name:        row.portfolioName,
          description: FUND_DESCRIPTION_OVERRIDES[inv?.id ?? -1]
            ?? FUND_DESCRIPTION_OVERRIDES_BY_SYMBOL[row.portfolioSymbol]
            ?? inv?.description ?? '',
          equityMin:   allEquity.length ? Math.min(...allEquity) : null,
          equityMax:   allEquity.length ? Math.max(...allEquity) : null,
        };
      });
    });
  }

  private computePivotRows(): void {
    if (!this.apiResult?.length) { this.pivotRows = []; return; }
    const invOrder = (symbol: string) => {
      const selectedIdx = this.availableInvestments.findIndex(
        inv => inv.symbol === symbol && this.selectedInvestmentNames.includes(inv.name)
      );
      if (selectedIdx !== -1) return selectedIdx;
      const idx = this.availableInvestments.findIndex(inv => inv.symbol === symbol);
      return idx === -1 ? this.availableInvestments.length : idx;
    };
    this.pivotRows = [...this.apiResult]
      .sort((a, b) => invOrder(a.portfolio) - invOrder(b.portfolio))
      .map(portfolio => {
        // Earliest inception date across all periods for this fund
        const allRows = this.resultPeriods
          .flatMap(p => p.rows.filter(r => r.portfolioSymbol === portfolio.portfolio));
        const inceptions = allRows.map(r => r.inceptionDate).filter((d): d is string => d !== null);
        const inceptionDate = inceptions.length
          ? inceptions.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0]
          : null;
        // Latest termination date across all periods for this fund
        const terminations = allRows.map(r => r.terminationDate).filter((d): d is string => d !== null);
        const terminationDate = terminations.length
          ? terminations.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
          : null;
        return {
          portfolioName:   this.getPortfolioDisplayName(portfolio.portfolio),
          portfolioSymbol: portfolio.portfolio,
          inceptionDate,
          terminationDate,
          cells: this.resultPeriods.map((_, i) =>
            portfolio.periods[i]
              ? { totalReturn: portfolio.periods[i].totalReturn,
                  annualizedReturn: portfolio.periods[i].annualizedReturn,
                  months: this.resultPeriods[i]?.months ?? portfolio.periods[i].months }
              : null
          )
        };
      });
  }

  private periodLabel(months: number | undefined): string {
    if (months === undefined || months === null) return '';
    const yrs = Math.floor(months / 12);
    const mo  = months % 12;
    const yrPart  = yrs  === 0 ? '' : yrs  === 1 ? '1 Year'  : `${yrs} Years`;
    const moPart  = mo   === 0 ? '' : mo   === 1 ? '1 Month' : `${mo} Months`;
    if (yrPart && moPart) return `${yrPart}, ${moPart}`;
    return yrPart || moPart || '0 Months';
  }

  // ── Row sort ─────────────────────────────────────────────────────────────

  /** 'default' = original investment-list order; 'vintage' = group by extracted year */
  resultSortMode: 'default' | 'vintage' = 'default';

  toggleResultSort(): void {
    this.resultSortMode = this.resultSortMode === 'default' ? 'vintage' : 'default';
    this.pushUrlParams();
  }

  // ── Scatter plot axis mode ────────────────────────────────────────────────

  /** 'fit' = auto-fit to data; 'origin' = include 0,0 in the domain */
  scatterOriginMode: 'fit' | 'origin' = 'fit';

  toggleScatterOrigin(): void {
    this.scatterOriginMode = this.scatterOriginMode === 'fit' ? 'origin' : 'fit';
    this.pushUrlParams();
    this.renderScatterForPeriod(this.activePeriodTab);
  }

  /** Extracts the trailing 4-digit year from a portfolio name, e.g. "... 2070" → 2070. */
  private extractVintageYear(name: string): number {
    const m = /\b(20\d{2}|19\d{2})\b(?!.*\b(20\d{2}|19\d{2})\b)/.exec(name);
    return m ? parseInt(m[1], 10) : 9999;
  }

  /**
   * Sort group for vintage mode:
   *   0 = Risk-based (IFC 80 / BM 80 / …)
   *   1 = Vintage target-date (has a target year)
   *   2 = Retirement Income
   */
  private vintageGroup(name: string, symbol: string): number {
    if (/risk\s+based/i.test(name)) return 0;
    if (/retirement\s+income/i.test(name) || symbol === 'td1933' || symbol === 'bm1933') return 2;
    return 1;
  }

  /** Returns true when the portfolio is a benchmark/comparison rather than an IFC fund. */
  private isBenchmark(name: string): boolean {
    return /^benchmark/i.test(name) || /^blended\s+prospectus/i.test(name);
  }

  /** Shared comparator: Risk-based (equity% desc) → vintage year desc → Retirement Income; IFC before Benchmark within each tier. */
  private vintageCompare(
    a: { portfolioName: string; portfolioSymbol: string },
    b: { portfolioName: string; portfolioSymbol: string }
  ): number {
    const ga = this.vintageGroup(a.portfolioName, a.portfolioSymbol);
    const gb = this.vintageGroup(b.portfolioName, b.portfolioSymbol);
    if (ga !== gb) return ga - gb;

    // Within risk-based tier: equity % descending, then IFC before Benchmark
    if (ga === 0) {
      const equityOf = (n: string) => { const m = /\b(\d+)\s*$/.exec(n); return m ? parseInt(m[1], 10) : 0; };
      const ea = equityOf(a.portfolioName);
      const eb = equityOf(b.portfolioName);
      if (ea !== eb) return eb - ea; // higher equity first
      return (this.isBenchmark(a.portfolioName) ? 1 : 0) - (this.isBenchmark(b.portfolioName) ? 1 : 0);
    }

    // Within vintage tier: year descending, then IFC before Benchmark
    if (ga === 1) {
      const ya = this.extractVintageYear(a.portfolioName);
      const yb = this.extractVintageYear(b.portfolioName);
      if (ya !== yb) return yb - ya;
      return (this.isBenchmark(a.portfolioName) ? 1 : 0) - (this.isBenchmark(b.portfolioName) ? 1 : 0);
    }

    // Within retirement-income tier: IFC before Benchmark
    return (this.isBenchmark(a.portfolioName) ? 1 : 0) - (this.isBenchmark(b.portfolioName) ? 1 : 0);
  }

  get sortedPivotRows(): typeof this.pivotRows {
    if (this.resultSortMode === 'default') return this.pivotRows;
    return [...this.pivotRows].sort((a, b) => this.vintageCompare(a, b));
  }

  sortedPeriodRows(rows: typeof this.resultPeriods[0]['rows']): typeof this.resultPeriods[0]['rows'] {
    if (this.resultSortMode === 'default') return rows;
    return [...rows].sort((a, b) => this.vintageCompare(a, b));
  }

  /** Returns period.rows in vintage sort order, each paired with its original index for data cell lookup. */
  sortedPeriodColumns(periodIndex: number): Array<{ row: ResultPeriodRow; index: number }> {
    const period = this.resultPeriods[periodIndex];
    if (!period) return [];
    const indexed = period.rows.map((row, index) => ({ row, index }));
    if (this.resultSortMode === 'default') return indexed;
    return indexed.sort((a, b) => this.vintageCompare(a.row, b.row));
  }

  /** Returns the base symbol without the `_gp` suffix, for use in SVG <use> hrefs. */
  svgSymbol(symbol: string): string {
    return symbol.replace(/_gp$/, '');
  }

  /**
   * Maps a monthly allocation symbol (e.g. 'port92srwf_ifa') to the color of the
   * matching IFC Index Portfolio, falling back to the supplied default.
   */
  private gpSegmentColor(monthlySymbol: string, fallback: string): string {
    const inv = this.availableInvestments.find(
      a => a.symbol === monthlySymbol && a.category === this.PORTFOLIOS_CATEGORY
    );
    return inv?.color || fallback;
  }

  private getPortfolioDisplayName(symbol: string): string {
    // When multiple investments share the same symbol (e.g. "Benchmark Risk Based Index
    // Portfolio 80" and "Dimensional Prospectus Benchmark Portfolio 80" both use
    // port80srwfbm_ifa), prefer whichever one the user actually selected so the result
    // row shows the name from the category the user chose from.
    const selectedMatch = this.availableInvestments.find(
      i => i.symbol === symbol && this.selectedInvestmentNames.includes(i.name)
    );
    return selectedMatch?.name
      ?? this.availableInvestments.find(i => i.symbol === symbol)?.name
      ?? symbol;
  }

  /** Returns the monthly-level rows for a period, indexed by date across all portfolios. */
  /** Parses equity % from a portfolio symbol, e.g. 'port92srwf_ifa' → 92, null for indexes */
  private parseEquity(symbol: string): number | null {
    const m = /^port(\d+)sr/.exec(symbol)
           ?? /^dfamf(\d+)$/.exec(symbol)
           ?? /^dfa_idp_sr_(\d+)$/.exec(symbol);
    return m ? parseInt(m[1], 10) : null;
  }

  periodMonthlyRows(periodIndex: number): Array<{
    date: string;
    values: Array<{ return: number | null; dollarValue: number; symbol: string; feeRateAnnual: number; feeRateMonthly: number; equity: number | null }>;
  }> {
    const period = this.resultPeriods[periodIndex];
    if (!period?.rows.length) return [];

    // Different API calls (regular vs TD/BM) may have different domain starts, so their
    // monthly arrays can have different lengths and start dates. Use date-keyed maps to
    // align each series by date rather than by array index.
    const rowMaps: Map<string, typeof period.rows[0]['monthly'][0]>[] =
      period.rows.map(r => {
        const m = new Map<string, typeof r.monthly[0]>();
        r.monthly.forEach(entry => m.set(entry.date, entry));
        return m;
      });

    // Union of all dates across all rows, sorted chronologically.
    const allDates = [...new Set(period.rows.flatMap(r => r.monthly.map(m => m.date)))]
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    return allDates.map(date => ({
      date,
      values: period.rows.map((r, ri) => {
        const entry = rowMaps[ri].get(date);
        const sym = entry?.symbol ?? '';
        return {
          return: entry?.return ?? null,
          dollarValue: entry?.dollarValue ?? 0,
          symbol: sym,
          feeRateAnnual: entry?.feeRateAnnual ?? 0,
          feeRateMonthly: entry?.feeRateMonthly ?? 0,
          equity: this.parseEquity(sym)
        };
      })
    }));
  }

  isAnnualDetail(i: number): boolean {
    return this.detailGranularity[i] === 'annual';
  }

  isRollingCycle(i: number): boolean {
    return this.annualCycleMode[i] === 'rolling';
  }

  setAnnualCycleMode(i: number, mode: 'calendar' | 'rolling'): void {
    if (this.annualCycleMode[i] === mode) return;
    this.annualCycleMode[i] = mode;
    this.pushUrlParams();
    setTimeout(() => this.renderChartForPeriod(i), 0);
  }

  setDetailGranularity(i: number, mode: 'monthly' | 'annual'): void {
    if (this.detailGranularity[i] === mode) return;
    this.detailGranularity[i] = mode;
    this.pushUrlParams();
    setTimeout(() => this.renderChartForPeriod(i), 0);
  }

  onShowFeeColChange(val: boolean): void {
    this.showFeeCol = val;
    this.pushUrlParams();
  }

  onShowFeeScheduleChange(val: boolean): void {
    this.showFeeSchedule = val;
    this.pushUrlParams();
  }

  onShowAllMonthlyRowsChange(tabIndex: number): void {
    this.showAllMonthlyRows[tabIndex] = true;
    this.pushUrlParams();
  }

  /** Annual aggregated rows: one row per 12-month period, either calendar year or rolling from period start. */
  periodAnnualRows(periodIndex: number): Array<{
    date: string;
    values: Array<{ return: number | null; dollarValue: number; symbol: string; feeRateAnnual: number; feeRateMonthly: number; equity: number | null }>;
  }> {
    const allRows = this.periodMonthlyRows(periodIndex);
    if (!allRows.length) return [];

    const rolling = this.isRollingCycle(periodIndex);

    // Group monthly rows into 12-month buckets
    let groups: (typeof allRows)[];
    if (rolling) {
      // Rolling: chunks of 12 months from the start; discard trailing partial chunk
      groups = [];
      for (let i = 0; i + 12 <= allRows.length; i += 12) {
        groups.push(allRows.slice(i, i + 12));
      }
    } else {
      // Calendar year: group by getFullYear()
      const yearMap = new Map<number, typeof allRows>();
      for (const row of allRows) {
        const yr = new Date(row.date).getFullYear();
        if (!yearMap.has(yr)) yearMap.set(yr, []);
        yearMap.get(yr)!.push(row);
      }
      groups = Array.from(yearMap.values());
    }

    return groups.map(monthRows => {
      const lastRow = monthRows[monthRows.length - 1];
      const annualValues = lastRow.values.map((v, colIdx) => {
        let compoundedReturn: number | null = null;
        let product = 1, valid = true;
        for (const mrow of monthRows) {
          const r = mrow.values[colIdx].return;
          if (r === null) { valid = false; break; }
          product *= (1 + r / 100);
        }
        if (valid) compoundedReturn = (product - 1) * 100;
        return { ...v, return: compoundedReturn };
      });
      return { date: lastRow.date, values: annualValues };
    });
  }

  // ── D3 chart rendering ────────────────────────────────────────────────────

  activePeriodTab = 0;
  chartStrokeWidth = 3;
  chartAxisFontSize=12;
  chartLogScale: boolean[] = [];
  monthlyViewMode: { value: boolean; return: boolean; equity: boolean }[] = [];

  toggleMonthlyView(i: number, col: 'value' | 'return' | 'equity'): void {
    const current = this.monthlyViewMode[i] ?? { value: true, return: false, equity: false };
    const updated = { ...current, [col]: !current[col] };
    if (!updated.value && !updated.return && !updated.equity) return;
    this.monthlyViewMode[i] = updated;
    this.pushUrlParams();
    setTimeout(() => this.renderChartForPeriod(i), 0);
  }

  isColActive(i: number, col: 'value' | 'return' | 'equity'): boolean {
    const m = this.monthlyViewMode[i];
    if (!m) return col === 'value';
    return m[col] ?? (col === 'value');
  }

  isLogScale(i: number): boolean {
    return this.chartLogScale[i] === true;
  }

  toggleLogScale(i: number): void {
    this.chartLogScale[i] = !this.chartLogScale[i];
    this.pushUrlParams();
    setTimeout(() => this.renderChartForPeriod(i), 0);
  }

  activeDetailCols(i: number): ('value' | 'return' | 'equity')[] {
    const cols: ('value' | 'return' | 'equity')[] = [];
    if (this.isColActive(i, 'value'))  cols.push('value');
    if (this.isColActive(i, 'return')) cols.push('return');
    if (this.isColActive(i, 'equity')) cols.push('equity');
    return cols.length ? cols : ['value'];
  }

  setActivePeriodTab(i: number): void {
    this.activePeriodTab = i;
    this.pushUrlParams();
    this.startCountAnimation(i);
    setTimeout(() => { this.renderChartForPeriod(i); this.renderScatterForPeriod(i); }, 0);
  }

  // ── URL state persistence ─────────────────────────────────────────────────

  /** Pending values set from URL params; consumed after submit() returns results. */
  private _pendingTab:         string | null = null;
  private _pendingDetail:      string | null = null;
  private _pendingLog:         string | null = null;
  private _pendingGranularity: string | null = null;
  private _pendingCycle:       string | null = null;
  private _pendingAllRows:     boolean | null = null;
  private _pendingShowFeeCol:  boolean | null = null;

  /** Preset label ↔ URL slug mappings (ordered to match user-facing preset buttons). */
  private readonly PRESET_SLUG_MAP = [
    { slug: 'ytd',  label: 'YTD'    },
    { slug: '1mo',  label: '1 Mo'   },
    { slug: '3mo',  label: '3 Mo'   },
    { slug: '6mo',  label: '6 Mo'   },
    { slug: '1yr',  label: '1 Yr'   },
    { slug: '3yr',  label: '3 Yr'   },
    { slug: '5yr',  label: '5 Yr'   },
    { slug: '10yr', label: '10 Yr'  },
    { slug: '15yr',        label: '15 Yr'                       },
    { slug: 'ifc-inception', label: 'Since IFC Inception 1/2009' },
  ] as const;

  private presetToSlug(label: string): string | null {
    return this.PRESET_SLUG_MAP.find(m => m.label === label)?.slug ?? null;
  }

  private slugToPreset(slug: string): typeof this.presets[0] | undefined {
    const entry = this.PRESET_SLUG_MAP.find(m => m.slug === slug.toLowerCase());
    if (!entry) return undefined;
    return this.presets.find(p => p.label === entry.label);
  }

  /**
   * Returns URL slugs for each period in FormArray order.
   * Preset rows → e.g. '3yr'; custom rows → 'YYYY-MM-DD_YYYY-MM-DD'.
   */
  private periodSlugs(): string[] {
    // Build reverse map: FormArray index → preset label
    const indexToLabel = new Map<number, string>();
    this.presetIndexMap.forEach((idx, label) => indexToLabel.set(idx, label));

    return this.timePeriods.controls.map((ctrl, idx) => {
      const v = ctrl.value;
      if (!v.isCustom) {
        const label = indexToLabel.get(idx);
        if (label) {
          const slug = this.presetToSlug(label);
          if (slug) return slug;
        }
      }
      // Custom period → YYYY-MM-DD_YYYY-MM-DD
      const sy = v.startYear;
      const sm = String(v.startMonth).padStart(2, '0');
      const ey = v.endYear;
      const em = String(v.endMonth).padStart(2, '0');
      const endDay = String(new Date(v.endYear, v.endMonth, 0).getDate()).padStart(2, '0');
      return `${sy}-${sm}-01_${ey}-${em}-${endDay}`;
    });
  }

  /** Reads URL query params and restores form state. Call after investments are loaded. */
  private applyUrlParams(): void {
    const qp = this.route.snapshot.queryParams;

    // Starting assets
    const assets = qp['assets'];
    if (assets) this.form.get('startingAssets')?.setValue(String(assets).replace(/,/g, ''));

    // Investments (tilde-separated numeric IDs or td-symbol for target-date)
    const invParam: string | undefined = qp['investments'];
    if (invParam) {
      const names: string[] = [];
      for (const token of invParam.split(/[,~]/)) {
        const t = token.trim();
        let inv: InvestmentChoice | undefined;
        if (/^\d+$/.test(t)) {
          const id = parseInt(t, 10);
          inv = this.availableInvestments.find(i => i.id === id);
        } else {
          // target-date symbol or fallback symbol lookup
          inv = this.availableInvestments.find(i => i.symbol === t);
        }
        if (inv) names.push(inv.name);
      }
      this.selectedInvestmentNames = names;

      // If any restored investment is hidden by the "show every 5th" filter, reveal all
      const hasHiddenSelected = names.some(name => {
        const inv = this.availableInvestments.find(i => i.name === name);
        return inv
          && (inv.category === this.PORTFOLIOS_CATEGORY || inv.category === this.GLIDE_PATH_CATEGORY)
          && !this.visibleInvestmentsByCategory(inv.category).some(v => v.name === name);
      });
      if (hasHiddenSelected) this.showAllPortfolios = true;
    }

    // Periods (comma-separated slugs/date-ranges)
    const periodsParam: string | undefined = qp['periods'];
    if (periodsParam) {
      while (this.timePeriods.length) this.timePeriods.removeAt(0);
      this.activePresets.clear();
      this.presetIndexMap.clear();

      for (const token of periodsParam.split(',')) {
        const t = token.trim();
        const preset = this.slugToPreset(t);
        if (preset) {
          this.togglePreset(preset);
        } else {
          // Custom range: YYYY-MM-DD_YYYY-MM-DD
          const m = /^(\d{4})-(\d{2})-\d{2}_(\d{4})-(\d{2})-\d{2}$/.exec(t);
          if (m) {
            this.timePeriods.push(this.fb.group({
              startMonth: [parseInt(m[2], 10)],
              startYear:  [parseInt(m[1], 10)],
              endMonth:   [parseInt(m[4], 10)],
              endYear:    [parseInt(m[3], 10)],
              isCustom:   [true]
            }));
          }
        }
      }
    }

    // Category visibility — restore before anything else so the picker reflects it
    const catsParam: string | undefined = qp['cats'];
    if (catsParam) {
      // URL explicitly specifies visible categories — override default
      const visible = catsParam.split(',').map(s => this.slugToCat(s.trim())).filter((n): n is string => n !== null);
      const allCats = [...new Set(this.availableInvestments.map(i => i.category))];
      this.hiddenCategories = new Set(allCats.filter(c => !visible.includes(c)));
    }

    // Ensure any category that contains a URL-restored selected investment is visible
    // so the picker shows the correct toggle state (e.g. Dimensional categories hidden by default)
    if (invParam) {
      for (const name of this.selectedInvestmentNames) {
        const inv = this.availableInvestments.find(i => i.name === name);
        if (inv) this.hiddenCategories.delete(inv.category);
      }
    }

    // Tab, detail, log scale, granularity, and cycle are stored as pending; applied after submit() populates results
    this._pendingTab         = qp['tab']         ?? null;
    this._pendingDetail      = qp['detail']      ?? null;
    this._pendingLog         = qp['log']         ?? null;
    this._pendingGranularity = qp['granularity'] ?? null;
    this._pendingCycle       = qp['cycle']       ?? null;

    // Fee column visibility (applied after submit resets showFeeCol)
    this._pendingShowFeeCol = qp['feecol'] === '1' ? true : null;

    // Sort mode
    if (qp['sort'] === 'vintage') this.resultSortMode = 'vintage';

    // Scatter axis mode
    if (qp['scatter'] === 'origin') this.scatterOriginMode = 'origin';

    // Fee params
    const flatFeeQp  = qp['flatFee'];
    const discountQp = qp['discount'];
    if (flatFeeQp !== undefined) { const parsed = parseFloat(flatFeeQp); this.feeFlatFee = parsed; this.scheduleFeeRate = parsed; }
    if (discountQp) { this.feeDiscount = parseFloat(discountQp); }
    if (flatFeeQp || discountQp) this.showFeeSchedule = true;

    // Show fee schedule toggle
    if (qp['feeschedule'] === '1') this.showFeeSchedule = true;

    // Advanced fee mode (flat fee / discount fields)
    if (qp['advfee'] === '1') this.advancedFeeMode = true;

    // Advisor fee toggle
    if (qp['advisorfee'] === '0') this.includeAdvisorFee = false;
    if (qp['advisorfee'] === '1') this.includeAdvisorFee = true;

    // Show all monthly rows (applied after submit populates results)
    this._pendingAllRows = qp['allrows'] === '1' ? true : null;
  }

  /** Serialises current UI state to URL query params (replaceUrl — no history entry). */
  private pushUrlParams(): void {
    if (!this.resultPeriods.length) return;

    const assets = String(this.form.get('startingAssets')?.value ?? '').replace(/,/g, '');
    const invIds = this.selectedInvestmentNames
      .map(n => this.availableInvestments.find(i => i.name === n))
      .filter(Boolean)
      .map(i => i!.id != null ? String(i!.id) : i!.symbol);

    const slugs      = this.periodSlugs();
    const activeSlug = slugs[this.activePeriodTab] ?? String(this.activePeriodTab);
    const mode       = this.activeDetailCols(this.activePeriodTab).join(',');

    const queryParams: Record<string, string> = {};
    if (assets && assets !== '0') queryParams['assets']      = assets;
    if (invIds.length)            queryParams['investments'] = invIds.join('~');
    if (slugs.length)             queryParams['periods']     = slugs.join(',');
    if (activeSlug)               queryParams['tab']         = activeSlug;
    queryParams['detail'] = mode;
    if (this.feeFlatFee !== null) queryParams['flatFee']    = String(this.feeFlatFee);
    if (this.feeDiscount > 0)  queryParams['discount']    = String(this.feeDiscount);
    if (this.showFeeSchedule)  queryParams['feeschedule'] = '1';
    if (this.advancedFeeMode)  queryParams['advfee']      = '1';
    if (!this.includeAdvisorFee) queryParams['advisorfee'] = '0';
    if (this.showAllMonthlyRows[this.activePeriodTab]) queryParams['allrows'] = '1';

    // Log scale — list of period slugs where log scale is active
    const logSlugs = this.periodSlugs().filter((_, i) => this.chartLogScale[i] === true);
    if (logSlugs.length) queryParams['log'] = logSlugs.join(',');

    // Granularity — list of period slugs where annual view is active
    const annualSlugs = this.periodSlugs().filter((_, i) => this.detailGranularity[i] === 'annual');
    if (annualSlugs.length) queryParams['granularity'] = annualSlugs.join(',');

    // Annual cycle — list of period slugs where rolling cycle is active
    const rollingSlugs = this.periodSlugs().filter((_, i) => this.annualCycleMode[i] === 'rolling');
    if (rollingSlugs.length) queryParams['cycle'] = rollingSlugs.join(',');

    // Fee column
    if (this.showFeeCol) queryParams['feecol'] = '1';

    // Sort mode
    if (this.resultSortMode === 'vintage') queryParams['sort'] = 'vintage';

    // Scatter axis mode
    if (this.scatterOriginMode === 'origin') queryParams['scatter'] = 'origin';

    // Preserve cats param if it was already set (category visibility is managed separately)
    const existingCats = new URLSearchParams(
      this.location.path().includes('?') ? (this.location.path().split('?')[1] ?? '') : ''
    ).get('cats');
    if (existingCats) queryParams['cats'] = existingCats;

    const queryString = Object.keys(queryParams)
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(queryParams[k])}`)
      .join('&');
    const path = this.location.path().split('?')[0];
    this.location.replaceState(path, queryString);
  }

  renderCharts(): void {
    this.renderChartForPeriod(this.activePeriodTab);
    this.renderScatterForPeriod(this.activePeriodTab);
  }

  private renderChartForPeriod(i: number): void {
    const period = this.resultPeriods[i];
    if (!period) return;
    const el = document.getElementById('chart-container') as HTMLElement | null;
    if (!el) return;
    d3.select(el).selectAll('*').remove();
    if (!period.rows.length) return;

    const cols    = this.activeDetailCols(i);
    const showVal = cols.includes('value');
    const showRet = cols.includes('return');
    const showEq  = cols.includes('equity');
    const multiType = cols.length > 1;

    // 1-month: fall through to line chart — dots are added after series draw

    const rows = period.rows;
    const isAnnual = this.isAnnualDetail(i);
    const isRolling = this.isRollingCycle(i);
    const effectiveMonthly: MonthlyReturn[][] = (() => {
      if (!isAnnual) return rows.map(r => r.monthly);
      const annualRows = this.periodAnnualRows(i);
      return rows.map((_, ri) =>
        annualRows.map(ar => ({
          date: ar.date,
          symbol: ar.values[ri]?.symbol ?? '',
          return: ar.values[ri]?.return ?? null,
          dollarValue: ar.values[ri]?.dollarValue ?? 0,
          feeRateAnnual: ar.values[ri]?.feeRateAnnual ?? 0,
          feeRateMonthly: ar.values[ri]?.feeRateMonthly ?? 0,
        } as MonthlyReturn))
      );
    })();
    const fallbackColors = ['#4e79a7','#f28e2b','#e15759','#76b7b2','#59a14f',
                            '#edc948','#b07aa1','#ff9da7','#9c755f','#bab0ac'];
    const colorOf = (idx: number): string => {
      const sym = rows[idx]?.portfolioSymbol;
      if (!sym || sym.startsWith('td')) return fallbackColors[idx % fallbackColors.length];
      const inv = this.availableInvestments.find(a => a.symbol === sym && !!a.color)
               ?? this.availableInvestments.find(a => a.symbol === sym);
      return inv?.color || fallbackColors[idx % fallbackColors.length];
    };

    const hasDollar = showVal;
    const hasPct    = showRet || showEq;
    const dualAxis  = hasDollar && hasPct;
    const useLogScale = this.isLogScale(i) && showVal;

    // Build legend entries — one per investment, with all active dash styles
    const legendSegLen = 12, legendSegGap = 4;
    type LegEntry = { label: string; color: string; dashes: (string | null)[] };
    const legendEntries: LegEntry[] = [];
    rows.forEach((row, idx) => {
      const dashes: (string | null)[] = [];
      if (showVal) dashes.push(null);
      if (showRet) dashes.push('5 3');
      if (showEq  && row.monthly.some(m => this.parseEquity(m.symbol) !== null)) dashes.push('2 2');
      legendEntries.push({ label: row.portfolioName, color: colorOf(idx), dashes });
    });

    const legendSwatchW   = 16;
    const legendSwatchGap = 5;
    const legendFontSize  = 11;
    const maxDashCount    = legendEntries.length ? Math.max(...legendEntries.map(e => e.dashes.length)) : 1;
    const totalSwatchW    = maxDashCount * legendSegLen + Math.max(0, maxDashCount - 1) * legendSegGap;
    const longestLabelW   = legendEntries.length
      ? Math.max(...legendEntries.map(e => e.label.length)) * legendFontSize * 0.52
      : 0;
    const monthCount  = effectiveMonthly[0]?.length ?? 0;
    const rightAxisW  = dualAxis ? 55 : 0;
    const margin = {
      top:    10,
      right:  rightAxisW + Math.ceil(totalSwatchW + legendSwatchGap + longestLabelW) + 20,
      bottom: monthCount <= 36 ? 65 : 40,
      left:   68
    };

    const w = Math.max(300, el.clientWidth - margin.left - margin.right);
    const h = 520 - margin.top - margin.bottom;
    const totalW = w + margin.left + margin.right;
    const totalH = h + margin.top  + margin.bottom;

    const svgRoot = d3.select(el)
      .append('svg')
      .attr('viewBox', `0 0 ${totalW} ${totalH}`)
      .attr('preserveAspectRatio', 'xMinYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const dropShadowUrl = 'url(#dropshadow)';

    const svg = svgRoot.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Only include dates where at least one series has a non-null return so the chart
    // starts (and ends) at the first (last) real data point rather than a run of nulls.
    const allDates = [...new Set(rows.flatMap((_, ri) => effectiveMonthly[ri].filter(m => m.return !== null).map(m => m.date)))]
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
      .map(d => new Date(d));
    const x = d3.scaleTime()
      .domain(d3.extent(allDates) as [Date, Date])
      .range([0, w]);

    // Y scales
    let yDollar: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number> | null = null;
    if (hasDollar) {
      // Exclude null-return entries from the domain so the Y scale isn't skewed by the
      // unchanged starting value that the API repeats for every null-return month.
      const vals = rows.flatMap((_, ri) => effectiveMonthly[ri].filter(m => m.return !== null).map(m => m.dollarValue));
      const vMin = d3.min(vals) ?? 0, vMax = d3.max(vals) ?? 0;
      if (useLogScale) {
        const domMin = Math.max(1, vMin * 0.95);
        const domMax = Math.max(domMin + 1, vMax * 1.05);
        yDollar = d3.scaleLog().domain([domMin, domMax]).range([h, 0]);
      } else {
        const vPad = (vMax - vMin) * 0.05 || 0.5;
        yDollar = d3.scaleLinear().domain([vMin - vPad, vMax + vPad]).range([h, 0]);
      }
    }
    let yPct: d3.ScaleLinear<number, number> | null = null;
    if (hasPct) {
      if (showEq && !showRet) {
        yPct = d3.scaleLinear().domain([0, 100]).range([h, 0]);
      } else {
        const pctVals: number[] = [];
        if (showRet) rows.forEach((r, ri) => effectiveMonthly[ri].forEach(m => { if (m.return !== null) pctVals.push(m.return!); }));
        if (showEq)  rows.forEach((r, ri) => effectiveMonthly[ri].forEach(m => { const eq = this.parseEquity(m.symbol); if (eq !== null) pctVals.push(eq); }));
        const pMin = pctVals.length ? d3.min(pctVals)! : 0;
        const pMax = pctVals.length ? d3.max(pctVals)! : 100;
        const pPad = (pMax - pMin) * 0.05 || 0.5;
        yPct = d3.scaleLinear().domain([pMin - pPad, pMax + pPad]).range([h, 0]);
      }
    }

    const yLeft  = hasDollar ? yDollar! : yPct!;
    const yRight = dualAxis  ? yPct!    : null;

    // Per-series clip-paths for staggered draw animation
    const staggerDelay = 120; // ms between each series
    const drawDuration = 900; // ms per series draw
    rows.forEach((_, ri) => {
      const cid = `chart-clip-${i}-${ri}`;
      svg.append('clipPath')
        .attr('id', cid)
        .append('rect')
        .attr('x', 0).attr('y', -10)
        .attr('height', h + 20)
        .attr('width', 0)
        .transition()
        .delay(ri * staggerDelay)
        .duration(drawDuration)
        .ease(d3.easeCubicInOut)
        .attr('width', w + 2);
    });

    // Draw series — each path/circle tagged with data-row=idx for hover dimming
    rows.forEach((row, idx) => {
      const isGp = row.portfolioSymbol.endsWith('_gp');
      const clipId = `chart-clip-${i}-${idx}`;

      /** Draw a single path element with the given datum and stroke color. */
      const drawPath = <T>(datum: T[], stroke: string, strokeW: number, dashArray: string | null,
                           lineGen: d3.Line<T>) => {
        svg.append('path')
          .datum(datum)
          .attr('fill', 'none')
          .attr('stroke', stroke)
          .attr('stroke-width', strokeW)
          .attr('stroke-dasharray', dashArray as any)
          .attr('clip-path', `url(#${clipId})`)
          .attr('filter', dropShadowUrl)
          .attr('data-row', idx)
          .attr('d', lineGen as any);
      };

      /**
       * For a GP row, split `monthly` into runs sharing the same allocation symbol
       * and draw each run with the matching IFC Index Portfolio color. An overlap
       * point is included so consecutive segments visually connect.
       */
      const drawGpSegments = (
        monthly: MonthlyReturn[],
        strokeW: number,
        dashArray: string | null,
        lineGen: d3.Line<MonthlyReturn>
      ) => {
        let si = 0;
        while (si < monthly.length) {
          const segSym = monthly[si].symbol;
          let ei = si + 1;
          while (ei < monthly.length && monthly[ei].symbol === segSym) ei++;
          // Include one overlap point from the next segment for smooth visual joins
          const pts = monthly.slice(si, ei < monthly.length ? ei + 1 : ei);
          drawPath(pts, this.gpSegmentColor(segSym, colorOf(idx)), strokeW, dashArray, lineGen);
          si = ei;
        }
      };

      if (showVal && yDollar) {
        const line = d3.line<MonthlyReturn>()
          .defined(d => d.return !== null)
          .x(d => x(new Date(d.date)))
          .y(d => yDollar!(d.dollarValue));
        if (isGp) {
          drawGpSegments(effectiveMonthly[idx], this.chartStrokeWidth, null, line);
        } else {
          drawPath(effectiveMonthly[idx], colorOf(idx), this.chartStrokeWidth, null, line);
        }
      }
      if (showRet && yPct) {
        const line = d3.line<MonthlyReturn>()
          .defined(d => d.return !== null)
          .x(d => x(new Date(d.date)))
          .y(d => yPct!(d.return ?? 0));
        if (isGp) {
          drawGpSegments(effectiveMonthly[idx], 1, multiType ? '5 3' : null, line);
        } else {
          drawPath(effectiveMonthly[idx], colorOf(idx), 1, multiType ? '5 3' : null, line);
        }
      }
      if (showEq && yPct) {
        const equityPoints = effectiveMonthly[idx]
          .map(m => ({ date: new Date(m.date), equity: this.parseEquity(m.symbol), symbol: m.symbol }))
          .filter(p => p.equity !== null) as Array<{ date: Date; equity: number; symbol: string }>;
        if (equityPoints.length) {
          if (isGp) {
            let si = 0;
            while (si < equityPoints.length) {
              const segSym = equityPoints[si].symbol;
              let ei = si + 1;
              while (ei < equityPoints.length && equityPoints[ei].symbol === segSym) ei++;
              const pts = equityPoints.slice(si, ei < equityPoints.length ? ei + 1 : ei);
              const line = d3.line<{ date: Date; equity: number; symbol: string }>()
                .x(d => x(d.date))
                .y(d => yPct!(d.equity));
              drawPath(pts, this.gpSegmentColor(segSym, colorOf(idx)),
                this.chartStrokeWidth, multiType ? '2 2' : null, line as any);
              si = ei;
            }
          } else {
            const line = d3.line<{ date: Date; equity: number; symbol: string }>()
              .x(d => x(d.date))
              .y(d => yPct!(d.equity));
            drawPath(equityPoints, colorOf(idx), this.chartStrokeWidth,
              multiType ? '2 2' : null, line as any);
          }
        }
      }

      // Single data point: draw a visible dot for each active series
      if (effectiveMonthly[idx].length === 1) {
        const pt = effectiveMonthly[idx][0];
        const ptX = x(new Date(pt.date));
        const r = Math.max(4, this.chartStrokeWidth * 2);
        if (showVal && yDollar) {
          svg.append('circle')
            .attr('cx', ptX).attr('cy', yDollar(pt.dollarValue)).attr('r', r)
            .attr('fill', colorOf(idx)).attr('stroke', 'white').attr('stroke-width', 1.5)
            .attr('data-row', idx);
        }
        if (showRet && yPct && pt.return !== null) {
          svg.append('circle')
            .attr('cx', ptX).attr('cy', yPct(pt.return!)).attr('r', r)
            .attr('fill', colorOf(idx)).attr('stroke', 'white').attr('stroke-width', 1.5)
            .attr('data-row', idx);
        }
        if (showEq && yPct) {
          const eq = this.parseEquity(pt.symbol);
          if (eq !== null) {
            svg.append('circle')
              .attr('cx', ptX).attr('cy', yPct(eq)).attr('r', r)
              .attr('fill', colorOf(idx)).attr('stroke', 'white').attr('stroke-width', 1.5)
              .attr('data-row', idx);
          }
        }
      }
    });

    // X axis
    const xAxis = isAnnual
      ? d3.axisBottom(x).tickValues(allDates).tickFormat((isRolling ? d3.timeFormat('%b %y') : d3.timeFormat('%Y')) as any)
      : monthCount <= 36
        ? d3.axisBottom(x).tickValues(allDates).tickFormat(d3.timeFormat('%b %y') as any)
        : d3.axisBottom(x).tickValues(allDates.filter(d => d.getMonth() === 11)).tickFormat(d3.timeFormat('%Y') as any);
    svg.append('g')
      .attr('transform', `translate(0,${h})`)
      .call(xAxis)
      .selectAll('text')
      .attr('transform', (isAnnual || monthCount > 36) ? 'rotate(-90), translate(-25,-20)' : 'rotate(-45), translate(-13,0)')
      .style('text-anchor', (isAnnual || monthCount > 36) ? 'middle' : 'end')
      .style('font-size', `${this.chartAxisFontSize}px`);

    // Left Y axis — log scale uses one tick per power-of-10 decade with short labels ($1K, $1M, etc.);
    // linear scale uses default tick count with full dollar labels.
    const dollarTickFormat = (d: d3.NumberValue): string => {
      const v = d as number;
      if (!hasDollar) return `${v.toFixed(1)}%`;
      const abs = Math.abs(v);
      if (abs >= 1e12) return `$${+(v / 1e12).toPrecision(3)}T`;
      if (abs >= 1e9)  return `$${+(v / 1e9 ).toPrecision(3)}B`;
      if (abs >= 1e6)  return `$${+(v / 1e6 ).toPrecision(3)}M`;
      if (abs >= 1e3)  return `$${+(v / 1e3 ).toPrecision(3)}K`;
      return `$${d3.format(',.0f')(v)}`;
    };
    svg.append('g')
      .call(
        useLogScale
          // Always show exactly 10 ticks, evenly spaced in log space across the domain
          ? (() => {
              const [dMin, dMax] = yLeft.domain() as number[];
              const logMin = Math.log10(dMin);
              const logMax = Math.log10(dMax);
              const tickVals = d3.range(0, 10).map(k =>
                Math.pow(10, logMin + (logMax - logMin) * k / 9)
              );
              return d3.axisLeft(yLeft as any)
                .tickValues(tickVals)
                .tickFormat(dollarTickFormat as any);
            })()
          : d3.axisLeft(yLeft as any)
              .tickFormat((d: any) => hasDollar ? dollarTickFormat(d) : `${(d as number).toFixed(1)}%`)
      )
      .selectAll('text')
      .style('font-size', `${this.chartAxisFontSize}px`);

    // Right Y axis (pct, when dual axis)
    if (yRight) {
      svg.append('g')
        .attr('transform', `translate(${w},0)`)
        .call(d3.axisRight(yRight).tickFormat(d => `${(d as number).toFixed(1)}%`))
        .selectAll('text')
        .style('font-size', `${this.chartAxisFontSize}px`);
    }

    // Legend — one row per investment, multiple line segments for each active metric
    const legX = w + rightAxisW + 8;
    legendEntries.forEach((entry, li) => {
      const ly = li * 18;
      const rowIdx = li; // legend index == row index

      const legGroup = svg.append('g')
        .style('cursor', 'pointer')
        .on('mouseenter', () => {
          // Dim all series except this one
          svg.selectAll<SVGElement, unknown>('path[data-row], circle[data-row]')
            .style('opacity', function() {
              return (this as SVGElement).getAttribute('data-row') === String(rowIdx) ? '1' : '0.05';
            });
          // Raise all elements of this row to the top
          svg.selectAll<SVGElement, unknown>(`path[data-row="${rowIdx}"], circle[data-row="${rowIdx}"]`)
            .raise();
          // Always keep tooltip layer above everything else
          svg.selectAll<SVGElement, unknown>('.ifc-tt-layer').raise();
        })
        .on('mouseleave', () => {
          svg.selectAll<SVGElement, unknown>('path[data-row], circle[data-row]')
            .style('opacity', '1');
        });

      let segX = legX;
      entry.dashes.forEach(dash => {
        legGroup.append('line')
          .attr('x1', segX).attr('y1', ly + legendSwatchW / 2)
          .attr('x2', segX + legendSegLen).attr('y2', ly + legendSwatchW / 2)
          .attr('stroke', entry.color).attr('stroke-width', 2)
          .attr('stroke-dasharray', dash as any)
           .style('filter', 'drop-shadow(0 1px 1px rgba(0,0,0,.2))');
        segX += legendSegLen + legendSegGap;
      });
      legGroup.append('text')
        .attr('x', legX + totalSwatchW + legendSwatchGap)
        .attr('y', ly + legendSwatchW - 1)
        .style('font-size', `${legendFontSize}px`)
        .attr('fill', '#333')
        .text(entry.label);
    });

    // Tooltip — one row per investment, all active metrics on one line
    // Build per-row maps keyed by ISO date string so different-length series
    // (e.g. regular vs TD/BM with different domain starts) are matched by date.
    const rowDateMaps: Map<string, MonthlyReturn>[] = rows.map((_, ri) => {
      const m = new Map<string, MonthlyReturn>();
      effectiveMonthly[ri].forEach(entry => m.set(entry.date, entry));
      return m;
    });
    // Bisect the union date list (same array used for the x-axis scale).
    const bisectDate = d3.bisector((d: Date) => d).left;
    const tooltip = svg.append('g').attr('class', 'ifc-tt-layer').attr('display', 'none')
      .style('pointer-events', 'none');
    const ttPadX = 10, ttPadY = 8, ttLineH = 16, ttDateH = ttLineH + 4;
    const ttSwatchSize = 10, ttSwatchGap = 5;
    const ttRect = tooltip.append('rect')
      .attr('rx', 4).attr('ry', 4)
      .attr('fill', 'white').attr('stroke', '#ccc').attr('stroke-width', 1)
      .style('filter', 'drop-shadow(0 1px 3px rgba(0,0,0,.2))');

    // One text element per row (investment)
    const ttTexts: d3.Selection<SVGTextElement, unknown, null, undefined>[] = [];
    rows.forEach((row, rowIdx) => {
      const textY = ttPadY + ttDateH + (rowIdx + 1) * ttLineH;
      tooltip.append('rect')
        .attr('x', ttPadX).attr('y', textY - ttSwatchSize + 2)
        .attr('width', ttSwatchSize).attr('height', ttSwatchSize)
        .attr('rx', 2).attr('ry', 2).attr('fill', colorOf(rowIdx))
        .style('filter', 'drop-shadow(0 1px 1px rgba(0,0,0,.5))');
      ttTexts.push(
        tooltip.append('text')
          .attr('x', ttPadX + ttSwatchSize + ttSwatchGap).attr('y', textY)
          .style('font-size', '11px').attr('fill', '#333')
      );
    });

    const ttDateText = tooltip.append('text')
      .attr('x', ttPadX).attr('y', ttPadY + ttLineH * 0.85)
      .style('font-size', '11px').style('font-weight', '600').attr('fill', '#333');
    const crosshair = svg.append('line')
      .attr('class', 'ifc-tt-layer')
      .attr('stroke', '#999').attr('stroke-width', 1).attr('stroke-dasharray', '3,3')
      .attr('y1', 0).attr('y2', h).attr('display', 'none')
      .style('pointer-events', 'none');
    // One dot per investment (positioned on the primary visible series)
    const dots = rows.map((_, rowIdx) =>
      svg.append('circle').attr('r', 4)
        .attr('class', 'ifc-tt-layer')
        .attr('fill', colorOf(rowIdx)).attr('stroke', 'white').attr('stroke-width', 1.5)
        .attr('display', 'none')
        .style('pointer-events', 'none')
    );

    svg.append('rect')
      .attr('width', w).attr('height', h)
      .attr('fill', 'none').style('pointer-events', 'all')
      .on('mouseenter', () => {
        tooltip.attr('display', null);
        crosshair.attr('display', null);
        dots.forEach(d => d.attr('display', null));
      })
      .on('mouseleave', () => {
        tooltip.attr('display', 'none');
        crosshair.attr('display', 'none');
        dots.forEach(d => d.attr('display', 'none'));
      })
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event);
        const hoveredDate = x.invert(mx);
        // Snap to the nearest date in the union date list
        const rightIdx = bisectDate(allDates, hoveredDate, 1);
        const leftIdx  = Math.max(0, rightIdx - 1);
        let snapIdx = leftIdx;
        if (rightIdx < allDates.length) {
          const ld = Math.abs(hoveredDate.getTime() - allDates[leftIdx].getTime());
          const rd = Math.abs(hoveredDate.getTime() - allDates[rightIdx].getTime());
          if (rd < ld) snapIdx = rightIdx;
        }
        const snappedDate = allDates[snapIdx];
        const snappedDateStr = snappedDate.toISOString().slice(0, 10); // YYYY-MM-DD prefix for map lookup
        const snappedX = x(snappedDate);
        crosshair.attr('x1', snappedX).attr('x2', snappedX);

        rows.forEach((row, rowIdx) => {
          // Look up this row's data point by date, not by position index
          const dateMap = rowDateMaps[rowIdx];
          // Try exact ISO key first, then fall back to a prefix match
          let pt = dateMap.get(snappedDate.toISOString());
          if (!pt) {
            // The stored date strings may not match exactly — find by prefix
            for (const [k, v] of dateMap) {
              if (k.startsWith(snappedDateStr)) { pt = v; break; }
            }
          }

          if (!pt) {
            ttTexts[rowIdx].text(row.portfolioName + ': n/a');
            return;
          }

          // Build "Name: $val  3.12%  45%" on one line
          let label = row.portfolioName + ':';
          if (showVal && yDollar) label += `  $${d3.format(',.0f')(pt.dollarValue)}`;
          if (showRet) label += `  ${pt.return !== null ? pt.return.toFixed(2) + '%' : 'n/a'}`;
          if (showEq)  {
            const eq = this.parseEquity(pt.symbol);
            label += `  eq:${eq !== null ? eq + '%' : 'n/a'}`;
          }
          ttTexts[rowIdx].text(label);

          // Dot: follow the first active/visible series — value → return → equity
          const dotY = (showVal && yDollar) ? yDollar(pt.dollarValue)
                     : (showRet && yPct && pt.return !== null) ? yPct(pt.return)
                     : (showEq  && yPct && this.parseEquity(pt.symbol) !== null) ? yPct(this.parseEquity(pt.symbol)!)
                     : 0;
          dots[rowIdx].attr('cx', snappedX).attr('cy', dotY);
        });

        ttDateText.text((isAnnual ? (isRolling ? d3.timeFormat('%b %Y') : d3.timeFormat('%Y')) : d3.timeFormat('%b %Y'))(snappedDate));
        const allNodes = [ttDateText.node(), ...ttTexts.map(t => t.node())] as SVGTextElement[];
        const maxTW = Math.max(...allNodes.map(n => n ? n.getBBox().width : 0));
        const ttW = ttPadX + ttSwatchSize + ttSwatchGap + maxTW + ttPadX;
        const ttH = ttPadY * 2 + ttDateH + rows.length * ttLineH;
        ttRect.attr('width', ttW).attr('height', ttH);
        let ttX = snappedX + 10;
        if (ttX + ttW > w) ttX = snappedX - ttW - 10;
        tooltip.attr('transform', `translate(${ttX},${Math.max(0, h / 2 - ttH / 2)})`);
      });
  }

  // ── Risk/Return scatter plot ───────────────────────────────────────────────

  private renderScatterForPeriod(i: number): void {
    const period = this.resultPeriods[i];
    const el = document.getElementById('scatter-container') as HTMLElement | null;
    if (!el) return;

    if (!period?.rows.length) {
      d3.select(el).selectAll('*').remove();
      return;
    }

    const useAnn = period.months >= 12;

    const fallbackColors = ['#4e79a7','#f28e2b','#e15759','#76b7b2','#59a14f',
                            '#edc948','#b07aa1','#ff9da7','#9c755f','#bab0ac'];
    const colorOf = (sym: string, origIdx: number): string => {
      if (!sym || sym.startsWith('td')) return fallbackColors[origIdx % fallbackColors.length];
      const inv = this.availableInvestments.find(a => a.symbol === sym && !!a.color)
               ?? this.availableInvestments.find(a => a.symbol === sym);
      return inv?.color || fallbackColors[origIdx % fallbackColors.length];
    };

    type ScatterPoint = { name: string; symbol: string; x: number; y: number; color: string };
    const sortedRows = this.resultSortMode === 'vintage'
      ? this.sortedPeriodRows(period.rows)
      : period.rows;
    const points: ScatterPoint[] = sortedRows
      .map((row) => {
        const origIdx = period.rows.indexOf(row);
        const sd  = (useAnn ? row.annualizedStdDev : row.stdDev) ?? null;
        const ret = (useAnn ? row.annualizedReturn  : row.totalReturn);
        if (sd === null) return null;
        return {
          name:   row.portfolioName,
          symbol: row.portfolioSymbol,
          x: sd,
          y: ret,
          color: colorOf(row.portfolioSymbol, origIdx),
        };
      })
      .filter((p): p is ScatterPoint => p !== null);

    if (!points.length) return;

    // ── Layout ───────────────────────────────────────────────────
    const fontSize = 12;
    const margin = { top: 20, right: 24, bottom: 52, left: 68 };
    const w = Math.max(300, el.clientWidth - margin.left - margin.right);
    const h = 300;
    const iconSize = 20;
    const half = iconSize / 2;
    const svgSymbolFn = (sym: string) => this.svgSymbol(sym);

    // ── Create SVG scaffold on first render; reuse on updates ────
    const isFirstRender = d3.select(el).select('svg.scatter-svg').empty();

    let svgRoot: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    let svg:     d3.Selection<SVGGElement,   unknown, null, undefined>;
    let ttDiv:   d3.Selection<HTMLDivElement, unknown, null, undefined>;

    if (isFirstRender) {
      d3.select(el).selectAll('*').remove();
      svgRoot = d3.select(el).append('svg').attr('class', 'scatter-svg');
      svg = svgRoot.append('g').attr('class', 'scatter-main')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      svg.append('g').attr('class', 'scatter-static');
      svg.append('g').attr('class', 'scatter-dots-layer');
      ttDiv = d3.select(el).append('div').attr('class', 'scatter-tooltip')
        .style('position', 'absolute').style('pointer-events', 'none')
        .style('background', 'rgba(255,255,255,0.97)').style('border', '1px solid #ccc')
        .style('border-radius', '4px').style('padding', '6px 10px')
        .style('font-size', '12px').style('line-height', '1.6')
        .style('white-space', 'nowrap').style('box-shadow', '0 2px 6px rgba(0,0,0,.12)')
        .style('display', 'none').style('z-index', '10');
    } else {
      svgRoot = d3.select(el).select<SVGSVGElement>('svg.scatter-svg');
      svg     = svgRoot.select<SVGGElement>('g.scatter-main');
      ttDiv   = d3.select(el).select<HTMLDivElement>('div.scatter-tooltip');
    }

    svgRoot
      .attr('viewBox', `0 0 ${w + margin.left + margin.right} ${h + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMinYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    // ── Scales ───────────────────────────────────────────────────
    const xVals = points.map(p => p.x);
    const yVals = points.map(p => p.y);
    const xPad = (Math.max(...xVals) - Math.min(...xVals) || 1) * 0.15;
    const yPad = (Math.max(...yVals) - Math.min(...yVals) || 1) * 0.20;

    let xDomainMin: number, xDomainMax: number, yDomainMin: number, yDomainMax: number;
    if (this.scatterOriginMode === 'origin') {
      xDomainMin = 0;
      xDomainMax = Math.max(...xVals) + xPad;
      yDomainMin = Math.min(0, Math.min(...yVals) - yPad);
      yDomainMax = Math.max(0, Math.max(...yVals) + yPad);
    } else {
      xDomainMin = Math.min(...xVals) - xPad;
      xDomainMax = Math.max(...xVals) + xPad;
      yDomainMin = Math.min(...yVals) - yPad;
      yDomainMax = Math.max(...yVals) + yPad;
    }

    const x = d3.scaleLinear().domain([xDomainMin, xDomainMax]).range([0, w]).nice();
    const y = d3.scaleLinear().domain([yDomainMin, yDomainMax]).range([h, 0]).nice();

    // ── Static layer: grid + axes + labels (always redrawn) ──────
    const staticLayer = svg.select<SVGGElement>('g.scatter-static');
    staticLayer.selectAll('*').remove();

    staticLayer.append('g').selectAll<SVGLineElement, number>('line')
      .data(y.ticks(5)).join('line')
      .attr('x1', 0).attr('x2', w)
      .attr('y1', d => y(d)).attr('y2', d => y(d))
      .attr('stroke', '#ebebeb').attr('stroke-width', 1).attr('stroke-dasharray', '2,5');

    staticLayer.append('g').selectAll<SVGLineElement, number>('line')
      .data(x.ticks(5)).join('line')
      .attr('y1', 0).attr('y2', h)
      .attr('x1', d => x(d)).attr('x2', d => x(d))
      .attr('stroke', '#ebebeb').attr('stroke-width', 1).attr('stroke-dasharray', '2,5');

    if (y.domain()[0] < 0 && y.domain()[1] > 0) {
      staticLayer.append('line')
        .attr('x1', 0).attr('x2', w).attr('y1', y(0)).attr('y2', y(0))
        .attr('stroke', '#bbb').attr('stroke-width', 1).attr('stroke-dasharray', '4,2');
    }

    staticLayer.append('g')
      .attr('transform', `translate(0,${h})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat(d => `${(d as number).toFixed(1)}%`))
      .call(g => g.selectAll('text').attr('font-size', fontSize));

    staticLayer.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => `${(d as number).toFixed(1)}%`))
      .call(g => g.selectAll('text').attr('font-size', fontSize));

    staticLayer.append('text')
      .attr('text-anchor', 'middle').attr('x', w / 2).attr('y', h + margin.bottom - 8)
      .attr('font-size', 12).attr('fill', '#555')
      .text(useAnn ? 'Ann. Std Dev (%)' : 'Std Dev (%)');

    staticLayer.append('text')
      .attr('text-anchor', 'middle')
      .attr('transform', `translate(${-margin.left + 18},${h / 2})rotate(-90)`)
      .attr('font-size', 12).attr('fill', '#555')
      .text(useAnn ? 'Ann. Return (%)' : 'Total Return (%)');

    // ── Dots layer: D3 enter / update / exit ─────────────────────
    const retLabel = useAnn ? 'Ann. Return' : 'Total Return';
    const sdLabel  = useAnn ? 'Ann. Std Dev' : 'Std Dev';
    const dotsLayer = svg.select<SVGGElement>('g.scatter-dots-layer');

    const dotSel = dotsLayer
      .selectAll<SVGGElement, ScatterPoint>('g.scatter-dot')
      .data(points, d => d.symbol);

    // EXIT — shrink in place, then remove
    dotSel.exit<ScatterPoint>()
      .transition().duration(200).ease(d3.easeCubicIn)
      .style('opacity', '0')
      .attrTween('transform', function() {
        const cur = d3.select(this).attr('transform') ?? 'translate(0,0) scale(1)';
        const translatePart = /translate\([^)]+\)/.exec(cur)?.[0] ?? 'translate(0,0)';
        return (t: number) => `${translatePart} scale(${1 - t})`;
      })
      .remove();

    // ENTER — appear at destination, scale 0→1, staggered
    const entered = dotSel.enter()
      .append('g')
      .attr('class', 'scatter-dot')
      .attr('transform', d => `translate(${x(d.x)},${y(d.y)}) scale(0.1)`)
      .style('opacity', '0')
      .style('cursor', 'crosshair');

    entered.each(function(d) {
      const useEl = d3.select(this).append('use')
        .attr('href', `#icon-${svgSymbolFn(d.symbol)}`)
        .attr('x', -half).attr('y', -half)
        .attr('width', iconSize).attr('height', iconSize);
      useEl.append('title').text('');
    });

    entered.transition()
      .duration(350)
      .delay((_, pi) => pi * 45)
      .ease(d3.easeCubicOut)
      .attr('transform', d => `translate(${x(d.x)},${y(d.y)}) scale(1)`)
      .style('opacity', '1');

    // UPDATE — move to new position
    dotSel.transition()
      .duration(500)
      .ease(d3.easeCubicInOut)
      .attr('transform', d => `translate(${x(d.x)},${y(d.y)}) scale(1)`)
      .style('opacity', '1');

    // Mouse events on all dots (entered + updated)
    entered.merge(dotSel)
      .on('mouseleave', () => { ttDiv.style('display', 'none'); })
      .on('mousemove', function(event: MouseEvent, d: ScatterPoint) {
        // Raise the hovered element to the top of the SVG stacking order so it
        // renders above all other dots, then show the tooltip.
        (this as SVGGElement).parentNode?.appendChild(this as SVGGElement);
        const rect = el.getBoundingClientRect();
        ttDiv
          .style('display', 'block')
          .style('left',  `${event.clientX - rect.left + 14}px`)
          .style('top',   `${event.clientY - rect.top  - 14}px`)
          .html(`<strong>${d.name}</strong><br>${retLabel}: <strong>${d.y.toFixed(2)}%</strong><br>${sdLabel}: <strong>${d.x.toFixed(2)}%</strong>`);
      });
  }

  private renderBarChart(
    container: HTMLElement,
    rows: Array<{ portfolioName: string; portfolioSymbol: string; totalReturn: number; endingDollar: number }>,
    mode: 'value' | 'return' = 'value'
  ): void {
    d3.select(container).selectAll('*').remove();
    if (!rows.length) return;

    const fs = this.chartAxisFontSize;

    // Dynamic left margin: wide enough for the largest y-axis tick label
    const yAccessor = (r: typeof rows[0]) =>
      mode === 'return' ? r.totalReturn : r.endingDollar;
    const yMax = d3.max(rows, yAccessor) ?? 0;
    const yMin = Math.min(0, d3.min(rows, yAccessor) ?? 0);
    const yPad = (yMax - yMin) * 0.1 || 0.5;
    const sampleLabel = mode === 'return'
      ? `${(yMax + yPad).toFixed(1)}%`
      : `$${d3.format(',.0f')(yMax + yPad)}`;
    const leftMargin = Math.ceil(sampleLabel.length * fs * 0.62) + 12;

    // Dynamic bottom margin: enough for rotated x-axis labels
    const longestName = Math.max(...rows.map(r => r.portfolioName.length));
    const bottomMargin = Math.ceil(longestName * fs * 0.62 * Math.sin(Math.PI * 40 / 180)) + 24;

    const margin = { top: 20, right: 20, bottom: bottomMargin, left: leftMargin };
    const w = Math.max(300, container.clientWidth - margin.left - margin.right);
    const h = 520 - margin.top - margin.bottom;

    const svg = d3.select(container)
      .append('svg')
      .attr('width',  w + margin.left + margin.right)
      .attr('height', h + margin.top  + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const fallbackColors = ['#4e79a7','#f28e2b','#e15759','#76b7b2','#59a14f',
                    '#edc948','#b07aa1','#ff9da7','#9c755f','#bab0ac'];
    const colorOf = (i: number) => {
      const sym = rows[i]?.portfolioSymbol;
      if (!sym || sym.startsWith('td')) return fallbackColors[i % fallbackColors.length];
      const inv = this.availableInvestments.find(a => a.symbol === sym);
      return inv?.color || fallbackColors[i % fallbackColors.length];
    };

    const x = d3.scaleBand()
      .domain(rows.map(r => r.portfolioName))
      .range([0, w])
      .padding(0.25);

    const y = d3.scaleLinear()
      .domain([yMin - yPad, yMax + yPad])
      .range([h, 0]);

    // Bar value labels: only show when bars are wide enough to avoid overlap
    const showBarLabels = x.bandwidth() >= 36;

    // Bars
    rows.forEach((row, i) => {
      const val = yAccessor(row);
      const barX = x(row.portfolioName) ?? 0;
      const barY = y(Math.max(0, val));
      const barH = Math.abs(y(0) - y(val));

      svg.append('rect')
        .attr('x', barX)
        .attr('y', y(0))
        .attr('width', x.bandwidth())
        .attr('height', 0)
        .attr('fill', colorOf(i))
        .transition().duration(450).ease(d3.easeQuadOut)
        .attr('y', barY)
        .attr('height', barH);

      if (showBarLabels) {
        const labelStr = mode === 'return'
          ? `${row.totalReturn.toFixed(2)}%`
          : `$${d3.format(',.0f')(row.endingDollar)}`;
        svg.append('text')
          .attr('x', barX + x.bandwidth() / 2)
          .attr('y', barY + (val >= 0 ? -4 : barH + 13))
          .attr('text-anchor', 'middle')
          .style('font-size', `${Math.max(9, fs - 2)}px`)
          .text(labelStr);
      }
    });

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${h})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .attr('transform', 'rotate(-40)')
      .style('text-anchor', 'end')
      .style('font-size', `${fs}px`);

    // Y axis
    svg.append('g')
      .call(d3.axisLeft(y).tickFormat(d =>
        mode === 'return'
          ? `${(d as number).toFixed(1)}%`
          : `$${d3.format(',.0f')(d as number)}`
      ))
      .selectAll('text')
      .style('font-size', `${fs}px`);
  }

  /** Plots equity % over time - subsumed into renderChartForPeriod.
   * @deprecated Retained here only as a non-called stub to avoid removing the parseEquity dependency.
   */
  private renderEquityChart_removed(
    container: HTMLElement,
    rows: Array<{ portfolioName: string; portfolioSymbol: string; monthly: MonthlyReturn[] }>
  ): void {
    d3.select(container).selectAll('*').remove();

    // Only include rows where at least one month has parseable equity
    const equityRows = rows
      .map(r => ({
        portfolioName:   r.portfolioName,
        portfolioSymbol: r.portfolioSymbol,
        points: r.monthly
          .map(m => ({ date: new Date(m.date), equity: this.parseEquity(m.symbol) }))
          .filter(p => p.equity !== null) as Array<{ date: Date; equity: number }>
      }))
      .filter(r => r.points.length > 0);

    if (!equityRows.length) {
      d3.select(container)
        .append('p')
        .style('font-size', '0.85em')
        .style('color', '#888')
        .text('No equity allocation data available for the selected investments.');
      return;
    }

    const legendSwatchW = 12;
    const legendSwatchGap = 6;
    const legendFontSize = 11;
    const longestLabelW = Math.max(...equityRows.map(r => r.portfolioName.length)) * legendFontSize * 0.62;
    const monthCount = equityRows[0].points.length;
    const margin = { top: 10, right: Math.ceil(legendSwatchW + legendSwatchGap + longestLabelW) + 16, bottom: monthCount <= 36 ? 65 : 40, left: 55 };
    const w = Math.max(300, container.clientWidth - margin.left - margin.right);
    const h = 520 - margin.top - margin.bottom;

    const svgRoot = d3.select(container)
      .append('svg')
      .attr('width',  w + margin.left + margin.right)
      .attr('height', h + margin.top + margin.bottom);

    const svg = svgRoot.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const allDates = equityRows[0].points.map(p => p.date);
    const x = d3.scaleTime().domain(d3.extent(allDates) as [Date, Date]).range([0, w]);
    const y = d3.scaleLinear().domain([0, 100]).range([h, 0]);

    const fallbackColors = ['#4e79a7','#f28e2b','#e15759','#76b7b2','#59a14f',
                            '#edc948','#b07aa1','#ff9da7','#9c755f','#bab0ac'];
    const colorOf = (i: number) => {
      const sym = equityRows[i]?.portfolioSymbol;
      if (!sym || sym.startsWith('td')) return fallbackColors[i % fallbackColors.length];
      const inv = this.availableInvestments.find(a => a.symbol === sym);
      return inv?.color || fallbackColors[i % fallbackColors.length];
    };

    const line = d3.line<{ date: Date; equity: number }>()
      .x(p => x(p.date))
      .y(p => y(p.equity));

    equityRows.forEach((row, i) => {
      const path = svg.append('path')
        .datum(row.points)
        .attr('fill', 'none')
        .attr('stroke', colorOf(i))
        .attr('stroke-width', 1.5)
        .attr('d', line as any);

      const totalLen = (path.node() as SVGPathElement).getTotalLength();
      path
        .attr('stroke-dasharray', `${totalLen} ${totalLen}`)
        .attr('stroke-dashoffset', totalLen)
        .transition().duration(500).ease(d3.easeLinear)
        .attr('stroke-dashoffset', 0);
    });

    // X axis
    const xAxis = monthCount <= 36
      ? d3.axisBottom(x).tickValues(allDates).tickFormat(d3.timeFormat('%b %y') as any)
      : d3.axisBottom(x).tickValues(allDates.filter(d => d.getMonth() === 11)).tickFormat(d3.timeFormat('%Y') as any);

    svg.append('g')
      .attr('transform',`translate(0,${h})`)
      .call(xAxis)
      .selectAll('text')
      .attr('transform', monthCount <= 36 ? 'rotate(-45), translate(-13,0)' : null)
      .style('text-anchor', monthCount <= 36 ? 'end' : 'middle');

    // Y axis (0–100%)
    svg.append('g')
      .call(d3.axisLeft(y).tickFormat(d => `${d}%`));

    // Legend
    equityRows.forEach((row, i) => {
      const ly = i * 18;
      svg.append('rect')
        .attr('x', w + 10).attr('y', ly).attr('width', legendSwatchW).attr('height', legendSwatchW)
        .attr('fill', colorOf(i));
      svg.append('text')
        .attr('x', w + 10 + legendSwatchW + legendSwatchGap).attr('y', ly + legendSwatchW - 1)
        .style('font-size', `${legendFontSize}px`)
        .attr('fill', '#333')
        .text(row.portfolioName);
    });

    // ── Tooltip ──────────────────────────────────────────────────────────────
    const bisect = d3.bisector((p: { date: Date; equity: number }) => p.date).left;

    const tooltip = svg.append('g').attr('display', 'none');
    const ttPadX = 10;
    const ttPadY = 8;
    const ttLineH = 16;
    const ttDateH = ttLineH + 4;
    const ttSwatchSize = 10;
    const ttSwatchGap  = 5;
    const ttTextX = ttPadX + ttSwatchSize + ttSwatchGap;

    const ttRect = tooltip.append('rect')
      .attr('rx', 4).attr('ry', 4)
      .attr('fill', 'white').attr('stroke', '#ccc').attr('stroke-width', 1)
      .style('filter', 'drop-shadow(0 1px 3px rgba(0,0,0,.2))');

    const ttSwatches: d3.Selection<SVGRectElement, unknown, null, undefined>[] = [];
    const ttTexts:   d3.Selection<SVGTextElement,  unknown, null, undefined>[] = [];
    equityRows.forEach((_, i) => {
      const textY = ttPadY + ttDateH + (i + 1) * ttLineH;
      ttSwatches.push(
        tooltip.append('rect')
          .attr('x', ttPadX).attr('y', textY - ttSwatchSize + 2)
          .attr('width', ttSwatchSize).attr('height', ttSwatchSize)
          .attr('rx', 2).attr('ry', 2).attr('fill', colorOf(i))
      );
      ttTexts.push(
        tooltip.append('text')
          .attr('x', ttTextX).attr('y', textY)
          .style('font-size', '11px').attr('fill', '#333')
      );
    });
    const ttDateText = tooltip.append('text')
      .attr('x', ttPadX).attr('y', ttPadY + ttLineH * 0.85)
      .style('font-size', '11px').style('font-weight', '600').attr('fill', '#333');

    const crosshair = svg.append('line')
      .attr('stroke', '#999').attr('stroke-width', 1).attr('stroke-dasharray', '3,3')
      .attr('y1', 0).attr('y2', h).attr('display', 'none');

    const dots = equityRows.map((_, i) =>
      svg.append('circle').attr('r', 4)
        .attr('fill', colorOf(i)).attr('stroke', 'white').attr('stroke-width', 1.5)
        .attr('display', 'none')
    );

    svg.append('rect')
      .attr('width', w).attr('height', h)
      .attr('fill', 'none').style('pointer-events', 'all')
      .on('mouseenter', () => {
        tooltip.attr('display', null);
        crosshair.attr('display', null);
        dots.forEach(d => d.attr('display', null));
      })
      .on('mouseleave', () => {
        tooltip.attr('display', 'none');
        crosshair.attr('display', 'none');
        dots.forEach(d => d.attr('display', 'none'));
      })
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event);
        const hoveredDate = x.invert(mx);

        // Snap to nearest point using first row as reference
        const pts = equityRows[0].points;
        const rightIdx = bisect(pts, hoveredDate, 1);
        const leftIdx  = rightIdx - 1;
        let idx = leftIdx;
        if (rightIdx < pts.length) {
          const leftDist  = Math.abs(hoveredDate.getTime() - pts[leftIdx].date.getTime());
          const rightDist = Math.abs(hoveredDate.getTime() - pts[rightIdx].date.getTime());
          if (rightDist < leftDist) idx = rightIdx;
        }
        const snappedX = x(pts[idx].date);
        crosshair.attr('x1', snappedX).attr('x2', snappedX);

        equityRows.forEach((row, i) => {
          const pt = row.points[idx];
          if (!pt) return;
          dots[i].attr('cx', snappedX).attr('cy', y(pt.equity));
          ttTexts[i].text(`${row.portfolioName}: ${pt.equity}%`);
        });

        ttDateText.text(d3.timeFormat('%b %Y')(pts[idx].date));

        const allNodes = [ttDateText.node(), ...ttTexts.map(t => t.node())] as SVGTextElement[];
        const maxTW = Math.max(...allNodes.map(n => n ? n.getBBox().width : 0));
        const ttW = ttPadX + ttSwatchSize + ttSwatchGap + maxTW + ttPadX;
        const ttH = ttPadY * 2 + ttDateH + equityRows.length * ttLineH;
        ttRect.attr('width', ttW).attr('height', ttH);

        let ttX = snappedX + 10;
        if (ttX + ttW > w) ttX = snappedX - ttW - 10;
        tooltip.attr('transform', `translate(${ttX},${Math.max(0, h / 2 - ttH / 2)})`);
      });
  }
}
